import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { FiExternalLink, FiInfo, FiLink } from "react-icons/fi";
import type {
  DataMartDimension,
  DataMartFact,
  DataMartGraph,
  SelectedDataMartEntity
} from "../model/types";
import type { CreateDataMartColumnInput } from "./types";
import {
  isCustomRdvTableConstruct,
  createTargetEntityDmColumns,
  fetchDataMartColumnAutocomplete,
  fetchTargetEntityDmSourceColumns,
  fetchTargetEntityDmColumns,
  saveTargetEntityMetadata,
  updateTargetEntityDmColumns,
  type DataMartColumnCreatePayload,
  type DataMartColumnUpdatePayload,
  type DataMartSourceColumnRecord,
  type TargetEntityRdvRecord
} from "../../../services/dataMartModelling.service";
import { SqlEditorField } from "../../mapping/components/forms/SqlEditorField";
import type { SqlAutocompleteContext } from "../../mapping/components/forms/SqlEditorField";
import { SqlPreview } from "../../mapping/components/forms/SqlPreview";
import type { ITargetEntityDmColumnRecord } from "../../../interfaces/dataMartModelling.interfaces";
import {
  DIMENSION_TABLE_CONSTRUCT_DEFAULT,
  DIMENSION_TABLE_CONSTRUCT_OPTIONS,
  FACT_TABLE_CONSTRUCT_DEFAULT,
  isSecondTemplateVersionOption,
  toDimensionTableConstructOption
} from "../constants";
import {
  DISTRIBUTION_TYPE_OPTIONS,
  getDistributionValidationError,
  isHashDistribution,
  isSqlPoolDedicated,
  normalizeDistributionType
} from "../../../helpers/distribution";
import { ColumnMappingActionCard } from "../../../components/modelling/ColumnMappingActionCard";
import {
  describePending,
  pendingChanges,
  signatureOf
} from "../../../helpers/pendingChanges";
import { ColumnMappingModalShell } from "../../../components/modelling/ColumnMappingModalShell";
import { MetadataActionFooter } from "../../../components/modelling/MetadataActionFooter";

const optionCollator = new Intl.Collator(undefined, {
  sensitivity: "base",
  numeric: true
});

const formatTitle = (entity: DataMartFact | DataMartDimension) => entity.record.target_entity_dm_name;

const roleLabel: Record<DataMartFact["columns"][number]["role"], string> = {
  measure: "Measure",
  attribute: "Attribute",
  dimension: "Dimension FK",
  degenerate: "Degenerate"
};

const roleBadgeColor: Record<DataMartFact["columns"][number]["role"], string> = {
  measure: "tw-bg-sky-100 tw-text-sky-700",
  attribute: "tw-bg-emerald-100 tw-text-emerald-700",
  dimension: "tw-bg-indigo-100 tw-text-indigo-700",
  degenerate: "tw-bg-slate-100 tw-text-slate-600"
};

type ColumnFilter = "all" | "measures" | "keys" | "attributes";
type ModalColumnViewFilter = "all" | "mapped" | "available" | "inherited" | "dim-key";

const filterColumn = (column: DataMartFact["columns"][number], filter: ColumnFilter) => {
  if (filter === "all") {
    return true;
  }
  if (filter === "measures") {
    return column.role === "measure";
  }
  if (filter === "keys") {
    return column.role === "dimension";
  }
  return column.role === "attribute";
};

type DraftColumnRow = CreateDataMartColumnInput & { id: string };

/** Everything about a data-mart column a modeller can change, as one comparable string. */
const draftRowSignature = (row: DraftColumnRow): string =>
  signatureOf([
    row.sourceName,
    row.name,
    row.reportViewName,
    row.description,
    row.columnTransform,
    row.isDimensionKey,
    row.isDimensionAttribute,
    row.isPii,
    row.target_entity_rdv_column_id,
    row.target_entity_dm_column_id
  ]);

type ToggleProps = {
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
  compact?: boolean;
};

const ToggleControl = ({ label, checked, onChange, disabled, compact = false }: ToggleProps) => (
  <div
    className={
      compact
        ? `tw-flex tw-w-full tw-justify-center ${disabled ? "tw-opacity-60" : ""}`
        : `tw-flex tw-items-center tw-gap-3 tw-rounded-2xl tw-border tw-border-slate-200 tw-bg-white tw-px-4 tw-py-2 ${disabled ? "tw-opacity-60" : ""
        }`
    }
  >
    <button
      type="button"
      aria-pressed={checked}
      aria-label={label}
      disabled={disabled}
      onClick={() => {
        if (!disabled) {
          onChange(!checked);
        }
      }}
      className={`tw-relative tw-inline-flex tw-items-center tw-rounded-full tw-transition ${compact ? "tw-h-5 tw-w-10" : "tw-h-6 tw-w-11"
        } ${checked ? "tw-bg-[#03234a]" : "tw-bg-slate-300"
        }`}
    >
      <span
        className={`tw-inline-block tw-transform tw-rounded-full tw-bg-white tw-shadow tw-transition ${compact ? "tw-h-4 tw-w-4" : "tw-h-5 tw-w-5"
          } ${checked ? (compact ? "tw-translate-x-5" : "tw-translate-x-5") : "tw-translate-x-1"
          }`}
      />
    </button>
    {!compact ? (
      <span
        className={`tw-text-xs tw-font-semibold tw-uppercase ${checked ? "tw-text-[#03234a]" : "tw-text-slate-500"
          }`}
      >
        {label}
      </span>
    ) : null}
  </div>
);

type MetadataDraft = {
  target_entity_dm_name: string;
  target_entity_dm_report_view_name: string;
  datamart: string;
  data_mart_id: string;
  table_construct: string;
  distribution_type: string;
  distribution_key: string;
  description: string;
  target_schema: string;
  target_entity_dm_report_view_source_filter: string;
  dq_source_filter: string;
  target_entity_rdv_id: string;
  target_entity_rdv_source_filter: string;
  generate_model_entity: boolean;
  include_dq_test: boolean;
  include_start_record: boolean;
  use_second_template_version: boolean;
};

const metadataInputClass =
  "tw-w-full tw-rounded-xl tw-border tw-border-slate-200 tw-bg-white tw-px-3.5 tw-py-2.5 tw-text-sm tw-font-medium tw-text-slate-900 tw-transition placeholder:tw-text-slate-400 focus:tw-border-[#0f172a] focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-[#0f172a]/10";

const tableInputClass =
  "tw-w-full tw-rounded-lg tw-border tw-border-slate-200 tw-bg-white tw-px-2.5 tw-py-1.5 tw-text-center tw-text-xs tw-font-medium tw-text-slate-800 focus:tw-border-[#03234a] focus:tw-ring-1 focus:tw-ring-[#03234a]";

type MetadataInputFieldProps = {
  label: string;
  value: string;
  onChange: (value: string) => void;
  multiline?: boolean;
  placeholder?: string;
  readOnly?: boolean;
};

const MetadataInputField = React.memo(({
  label,
  value,
  onChange,
  multiline = false,
  placeholder,
  readOnly = false
}: MetadataInputFieldProps) => (
  <div className="tw-group tw-flex tw-flex-col tw-gap-1.5 tw-border-b tw-border-slate-100 tw-pb-4">
    <label className="tw-text-[0.6rem] tw-font-semibold tw-uppercase tw-tracking-[0.3em] tw-text-slate-500">
      {label}
    </label>
    {multiline ? (
      <textarea
        value={value}
        onChange={(event) => onChange(event.target.value)}
        rows={3}
        placeholder={placeholder}
        className={`${metadataInputClass} tw-min-h-[6rem]`}
        readOnly={readOnly}
      />
    ) : (
      <input
        type="text"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
        className={metadataInputClass}
        readOnly={readOnly}
      />
    )}
  </div>
), (previous, next) =>
  previous.label === next.label &&
  previous.value === next.value &&
  previous.multiline === next.multiline &&
  previous.placeholder === next.placeholder &&
  previous.readOnly === next.readOnly
);

type MetadataSelectOption = { label: string; value: string };
type DataMartOption = { data_mart_id: number; data_mart: string };

type MetadataSelectFieldProps = {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: ReadonlyArray<MetadataSelectOption>;
  placeholder?: string;
  disabled?: boolean;
};

const MetadataSelectField = React.memo(({
  label,
  value,
  onChange,
  options,
  placeholder,
  disabled
}: MetadataSelectFieldProps) => (
  <div className="tw-group tw-flex tw-flex-col tw-gap-1.5 tw-border-b tw-border-slate-100 tw-pb-4">
    <label className="tw-text-[0.6rem] tw-font-semibold tw-uppercase tw-tracking-[0.3em] tw-text-slate-500">
      {label}
    </label>
    <select
      value={value}
      onChange={(event) => onChange(event.target.value)}
      className={metadataInputClass}
      disabled={disabled}
    >
      {placeholder && (
        <option value="" disabled>
          {placeholder}
        </option>
      )}
      {options.map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
  </div>
), (previous, next) =>
  previous.label === next.label &&
  previous.value === next.value &&
  previous.options === next.options &&
  previous.placeholder === next.placeholder &&
  previous.disabled === next.disabled
);

const InlineCreateTextButton = ({
  onClick,
  children
}: {
  onClick: () => void;
  children: React.ReactNode;
}) => (
  <button
    type="button"
    onClick={onClick}
    className="tw-inline-flex tw-items-center tw-gap-1 tw-appearance-none tw-border-0 tw-bg-transparent tw-p-0 tw-text-left tw-text-[13px] tw-font-semibold tw-text-[#03234a] tw-underline tw-decoration-[#03234a]/35 tw-underline-offset-2 hover:tw-text-[#021b39] hover:tw-decoration-[#021b39] focus:tw-outline-none"
  >
    <span aria-hidden="true" className="tw-text-[15px] tw-leading-none">+</span>
    {children}
  </button>
);

export const buildMetadataDraft = (
  record?: DataMartFact["record"],
  entityType?: SelectedDataMartEntity["type"]
): MetadataDraft => {
  const tableConstruct = record?.table_construct?.trim() ?? "";
  const useSecondTemplateVersion =
    record?.use_second_template_version != null ? Boolean(record.use_second_template_version) : false;
  const resolvedTableConstruct =
    entityType === "fact"
      ? FACT_TABLE_CONSTRUCT_DEFAULT
      : entityType === "dimension"
        ? toDimensionTableConstructOption(
            tableConstruct || DIMENSION_TABLE_CONSTRUCT_DEFAULT,
            useSecondTemplateVersion
          )
        : tableConstruct;

  return {
    target_entity_dm_name: record?.target_entity_dm_name ?? "",
    target_entity_dm_report_view_name:
      record?.target_entity_dm_report_view_name ??
      record?.target_entity_dm_name ??
      "",
    datamart: record?.datamart ?? "",
    data_mart_id: record?.data_mart_id != null ? String(record.data_mart_id) : "",
    table_construct: resolvedTableConstruct,
    // Canonical T-SQL token — the generator writes this straight into
    // WITH (DISTRIBUTION = ...), so label text ("hash key") would not parse.
    distribution_type: normalizeDistributionType(record?.distribution_type),
    distribution_key: record?.distribution_key ?? "",
    description: record?.description ?? "",
    target_schema: record?.target_schema ?? "",
    target_entity_dm_report_view_source_filter:
      record?.target_entity_dm_report_view_source_filter ?? "",
    dq_source_filter: record?.dq_source_filter ?? "",
    target_entity_rdv_id:
      record?.target_entity_rdv_id != null ? String(record.target_entity_rdv_id) : "",
    target_entity_rdv_source_filter: record?.target_entity_rdv_source_filter ?? "",
    generate_model_entity:
      record?.generate_model_entity != null ? resolveBooleanFlag(record.generate_model_entity) : true,
    include_dq_test:
      record?.include_dq_test != null
        ? Boolean(record.include_dq_test)
        : !Boolean(record?.exclude_dq_test),
    include_start_record: record?.include_start_record != null ? Boolean(record.include_start_record) : false,
    use_second_template_version: useSecondTemplateVersion
  };
};

/**
 * The save always sends use_second_template_version, and the backend lets that flag win over
 * the "_V2_" spelling — so the flag has to follow the Table Type the modeller picks.
 */
export const applyDimensionTableConstructChoice = (
  draft: MetadataDraft,
  value: string
): MetadataDraft => ({
  ...draft,
  table_construct: value,
  use_second_template_version: isSecondTemplateVersionOption(value)
});

const areMetadataDraftsEqual = (first: MetadataDraft, second: MetadataDraft) =>
  first.target_entity_dm_name === second.target_entity_dm_name &&
  first.target_entity_dm_report_view_name === second.target_entity_dm_report_view_name &&
  first.data_mart_id === second.data_mart_id &&
  first.table_construct === second.table_construct &&
  first.distribution_type === second.distribution_type &&
  first.distribution_key === second.distribution_key &&
  first.description === second.description &&
  first.target_schema === second.target_schema &&
  first.target_entity_dm_report_view_source_filter ===
  second.target_entity_dm_report_view_source_filter &&
  first.dq_source_filter === second.dq_source_filter &&
  first.target_entity_rdv_id === second.target_entity_rdv_id &&
  first.target_entity_rdv_source_filter === second.target_entity_rdv_source_filter &&
  first.generate_model_entity === second.generate_model_entity &&
  first.include_dq_test === second.include_dq_test &&
  first.include_start_record === second.include_start_record &&
  first.use_second_template_version === second.use_second_template_version;

const deriveColumnRoleFromFlags = (
  isDimensionKey: boolean,
  isDimensionAttribute: boolean
): DataMartFact["columns"][number]["role"] => {
  if (isDimensionKey) {
    return "dimension";
  }
  if (isDimensionAttribute) {
    return "attribute";
  }
  return "measure";
};

const buildDimensionAttributeValue = (
  role: DataMartFact["columns"][number]["role"]
): DataMartColumnCreatePayload["dimension_attribute"] => {
  if (role === "attribute") {
    return true;
  }
  return false;
};

const getColumnOriginLabel = (value?: string | null) => {
  const normalized = (value ?? "").trim().toLowerCase();
  if (normalized === "relationship_generated") {
    return "Generated";
  }
  if (normalized === "manual") {
    return "Manual";
  }
  return null;
};

const getColumnOriginBadgeClass = (value?: string | null) => {
  const normalized = (value ?? "").trim().toLowerCase();
  if (normalized === "relationship_generated") {
    return "tw-bg-amber-100 tw-text-amber-700";
  }
  if (normalized === "manual") {
    return "tw-bg-slate-100 tw-text-slate-700";
  }
  return "tw-bg-slate-100 tw-text-slate-600";
};

const getDraftOrigin = (row: DraftColumnRow): { label: string; tone: string; detail?: string } => {
  if (row.isInherited) {
    return {
      label: "Available",
      tone:
        row.inheritanceType === "satellite_parent_hub_hash_key"
          ? "tw-bg-emerald-100 tw-text-emerald-700"
          : "tw-bg-indigo-100 tw-text-indigo-700",
      detail:
        row.inheritanceType === "satellite_parent_hub_hash_key"
          ? "Hub hash"
          : row.inheritanceType === "satellite_parent_hub_business_key"
            ? "Inherited BK"
            : undefined
    };
  }
  if (row.target_entity_rdv_column_id == null) {
    return { label: "Manual", tone: "tw-bg-slate-100 tw-text-slate-700" };
  }
  return { label: "Available", tone: "tw-bg-sky-100 tw-text-sky-700" };
};

const toNullableField = (value: string) => {
  const trimmed = value.trim();
  return trimmed ? trimmed : null;
};

const buildColumnCreatePayload = (
  row: CreateDataMartColumnInput,
  targetEntityDmId: number
): DataMartColumnCreatePayload => {
  const normalizedName = row.name.trim();
  const normalizedReportViewName = row.reportViewName.trim() || normalizedName;
  const derivedRole = deriveColumnRoleFromFlags(row.isDimensionKey, row.isDimensionAttribute);
  return {
    target_entity_dm_id: targetEntityDmId,
    target_entity_dm_column_name: normalizedName,
    target_entity_dm_report_view_column_name: normalizedReportViewName,
    description: toNullableField(row.description),
    target_entity_dm_column_transform: toNullableField(row.columnTransform),
    target_entity_dm_report_view_column_transform: null,
    dimension_key: row.isDimensionKey ? true : null,
    dimension_attribute: buildDimensionAttributeValue(derivedRole),
    pii: row.isPii,
    is_active: true,
    target_entity_rdv_column_id: row.target_entity_rdv_column_id ?? null,
    target_entity_dm_column_id: row.target_entity_dm_column_id ?? null
  };
};

const resolveBooleanFlag = (value: string | boolean | null | undefined) => {
  if (typeof value === "boolean") {
    return value;
  }
  if (typeof value === "string") {
    const normalized = value.trim().toUpperCase();
    if (!normalized) {
      return false;
    }
    return normalized === "Y" || normalized === "YES" || normalized === "TRUE";
  }
  return false;
};

const hasDimensionKeyValue = (value: string | boolean | null | undefined) => {
  if (typeof value === "boolean") {
    return value;
  }
  if (typeof value === "string") {
    const normalized = value.trim().toUpperCase();
    if (!normalized) {
      return false;
    }
    return normalized !== "SELF";
  }
  return false;
};

const hasDimensionAttributeValue = (column: DataMartFact["columns"][number]) => {
  if (column.role === "measure") {
    return false;
  }
  const raw = column.record.dimension_attribute;
  if (typeof raw === "boolean") {
    return raw;
  }
  const normalized = raw?.trim().toUpperCase();
  if (!normalized) {
    return column.role === "attribute";
  }
  if (normalized === "MEASURE") {
    return false;
  }
  return normalized === "Y";
};

const buildDraftRowFromRdvColumn = (
  record: DataMartSourceColumnRecord,
  entityId: string,
  options?: { preferDimensionKey?: boolean; preferredTargetName?: string }
): DraftColumnRow => {
  const sourceName =
    record.target_entity_rdv_column_name ??
    record.source_entity_column_name ??
    "";
  const fallbackTargetName =
    record.target_entity_rdv_column_name ??
    record.source_entity_column_name ??
    "";
  const normalizedFallbackTarget = (fallbackTargetName ?? "").trim();
  const descriptionValue = record.description ?? "";
  const transformValue = "";
  const isInherited = Boolean(record.is_inherited);
  // A dimension is keyed on the HASH, never on a business key.
  //
  // This used to default Dim Key from `business_key`, so every business key the source
  // entity carried arrived pre-toggled as a dimension key — four of them on a typical
  // satellite. That is what put more than one dimension key on 86 dimensions, which
  // `_get_single_active_dimension_key_column_or_400` then rejects when the dimension is
  // wired to a fact. The hash is the column a fact actually joins on, so it is the only one
  // that should arrive switched on; business keys are attributes like any other.
  const isDimensionKey = options?.preferDimensionKey
    ? true
    : resolveBooleanFlag(record.hash_key);
  const preferredTargetName = (options?.preferredTargetName ?? "").trim();
  const effectiveSourceName = sourceName ?? "";
  const effectiveTargetName =
    options?.preferDimensionKey && preferredTargetName
      ? preferredTargetName
      : normalizedFallbackTarget || sourceName || "";
  return {
    id: `rdv-${record.target_entity_rdv_column_id ?? record.source_target_entity_rdv_column_mapping_id ?? Date.now()}`,
    entityId,
    sourceName: effectiveSourceName,
    name: effectiveTargetName,
    reportViewName: effectiveTargetName,
    description: descriptionValue ?? "",
    columnTransform: transformValue ?? "",
    isDimensionKey,
    isDimensionAttribute: !isDimensionKey,
    isPii: resolveBooleanFlag(record.pii),
    target_entity_rdv_column_id: record.target_entity_rdv_column_id ?? null,
    target_entity_dm_column_id: null,
    isInherited,
    inheritanceType: record.inheritance_type ?? null,
    inheritedFromName: record.inherited_from_target_entity_rdv_name ?? null
  };
};

const getSourceRowPriority = (record: DataMartSourceColumnRecord) => {
  const inheritanceType = (record.inheritance_type ?? "").trim().toLowerCase();
  if (inheritanceType === "satellite_parent_hub_hash_key") {
    return 0;
  }
  if (inheritanceType === "satellite_parent_hub_business_key") {
    return 1;
  }
  return 2;
};

const sortDmSourceRows = (rows: DataMartSourceColumnRecord[]) =>
  [...rows].sort((left, right) => {
    const priorityDifference = getSourceRowPriority(left) - getSourceRowPriority(right);
    if (priorityDifference !== 0) {
      return priorityDifference;
    }
    const leftName = String(
      left.target_entity_rdv_column_name ??
      left.source_entity_column_name ??
      ""
    );
    const rightName = String(
      right.target_entity_rdv_column_name ??
      right.source_entity_column_name ??
      ""
    );
    return leftName.localeCompare(rightName, undefined, { sensitivity: "base" });
  });

const isHashKey = (record: DataMartSourceColumnRecord) =>
  resolveBooleanFlag(record.hash_key) ||
  (Boolean(record.is_inherited) &&
    (record.inheritance_type ?? "").trim().toLowerCase() === "satellite_parent_hub_hash_key");

// A dimension is keyed on its hash, and the key is named `<dimension>_key` (d_sales_order ->
// d_sales_order_key). That holds whichever row carries the hash: the parent hub's, lent to a
// satellite that has none, or the source entity's own — which every satellite has had since
// the hash backfill of 2026-08-26, and which used to keep its raw `<ensemble>_h_hash` name.
// Only when there is exactly one candidate and no key is saved yet; with several (a link's
// hub hashes), which one keys the dimension is the modeller's choice.
export const buildDimensionDraftRows = (
  sourceRows: DataMartSourceColumnRecord[],
  options: { entityId: string; dimensionName: string; hasPersistedDimensionKey: boolean }
): DraftColumnRow[] => {
  const hashCandidates = sourceRows.filter(isHashKey);
  const keyRecord =
    !options.hasPersistedDimensionKey && hashCandidates.length === 1 ? hashCandidates[0] : null;
  const dimensionName = options.dimensionName.trim();
  const keyName = dimensionName ? `${dimensionName}_key` : "";
  return sortDmSourceRows(sourceRows).map((record) =>
    buildDraftRowFromRdvColumn(
      record,
      options.entityId,
      record === keyRecord ? { preferDimensionKey: true, preferredTargetName: keyName } : undefined
    )
  );
};

type ExistingColumnEditState = {
  columnName: string;
  reportViewColumnName: string;
  description: string;
  columnTransform: string;
  dimensionKey: boolean;
  dimensionAttribute: boolean;
  pii: boolean;
};

const toInputValue = (value?: string | null) => value?.trim() ?? "";

const buildExistingColumnEditState = (
  column: DataMartFact["columns"][number]
): ExistingColumnEditState => ({
  columnName: toInputValue(column.record.target_entity_dm_column_name),
  reportViewColumnName: toInputValue(column.record.target_entity_dm_column_name),
  description: toInputValue(column.record.description),
  columnTransform: toInputValue(column.record.target_entity_dm_column_transform),
  dimensionKey: hasDimensionKeyValue(column.record.dimension_key),
  dimensionAttribute: hasDimensionAttributeValue(column),
  pii: resolveBooleanFlag(column.record.pii)
});

const hasExistingColumnChanged = (
  column: DataMartFact["columns"][number],
  edits: ExistingColumnEditState
) => {
  const baseline = buildExistingColumnEditState(column);
  return (
    baseline.columnName !== edits.columnName ||
    baseline.reportViewColumnName !== edits.reportViewColumnName ||
    baseline.description !== edits.description ||
    baseline.columnTransform !== edits.columnTransform ||
    baseline.dimensionKey !== edits.dimensionKey ||
    baseline.dimensionAttribute !== edits.dimensionAttribute ||
    baseline.pii !== edits.pii
  );
};

export function DataMartEntityPanel({
  selectedEntity,
  currentGraph,
  onClose,
  onUpdateEntity,
  onDeleteEntity,
  onRemoveRelationship,
  onSyncColumns,
  relatedRdvEntities,
  isLoadingRdvEntities,
  rdvEntitiesError,
  onReloadRdvEntities,
  dataMartOptions,
  isLoadingDataMarts,
  dataMartsError,
  onReloadDataMarts,
  onCreateDataMart,
  activeDwTargetType
}: {
  selectedEntity: SelectedDataMartEntity;
  currentGraph: DataMartGraph | null;
  onClose: () => void;
  onUpdateEntity: (entityId: string, updates: Partial<DataMartFact["record"]>) => void;
  onDeleteEntity: (
    entityId: string,
    entityType: "fact" | "dimension",
    targetEntityDmId?: number | null
  ) => Promise<void>;
  onRemoveRelationship?: (factEntityId: string, dimensionEntityId: string) => void;
  onSyncColumns: (entityId: string, columns: ITargetEntityDmColumnRecord[]) => void;
  relatedRdvEntities: TargetEntityRdvRecord[];
  isLoadingRdvEntities: boolean;
  rdvEntitiesError: string | null;
  onReloadRdvEntities?: () => void;
  dataMartOptions: DataMartOption[];
  isLoadingDataMarts: boolean;
  dataMartsError: string | null;
  onReloadDataMarts?: () => void;
  onCreateDataMart: (name: string) => Promise<{ ok: boolean; option?: DataMartOption; error?: string }>;
  activeDwTargetType?: string | null;
}): JSX.Element | null {
  const selectedEntityType = selectedEntity.type;
  const entity = selectedEntity.entity;
  const factEntity =
    selectedEntityType === "fact" && selectedEntity ? selectedEntity.entity : null;
  const dimensionEntity =
    selectedEntityType === "dimension" && selectedEntity ? selectedEntity.entity : null;
  const isFactEntity = selectedEntityType === "fact";
  const isDimensionEntity = selectedEntityType === "dimension";
  const [activeTab, setActiveTab] = useState<"details" | "mappings">("details");
  const columnFilter: ColumnFilter = "all";
  const [metadataError, setMetadataError] = useState<string | null>(null);
  const [validationPopupMessage, setValidationPopupMessage] = useState<string | null>(null);
  const [isSavingMetadata, setIsSavingMetadata] = useState(false);
  const [resolvedTargetEntityDmId, setResolvedTargetEntityDmId] = useState<number | null>(
    entity?.record.target_entity_dm_id ?? null
  );
  const [metadataDraft, setMetadataDraft] = useState<MetadataDraft>(() =>
    buildMetadataDraft(entity?.record, selectedEntityType)
  );
  const tableConstructValue = metadataDraft.table_construct;
  const normalizedDistributionType = normalizeDistributionType(
    metadataDraft.distribution_type
  );
  const isDistributionKeyEditable = isHashDistribution(normalizedDistributionType);
  const showDistributionControls = isSqlPoolDedicated(activeDwTargetType);
  const [draftRows, setDraftRows] = useState<DraftColumnRow[]>([]);
  /**
   * What the grid looked like when it loaded, so a row can say whether it is stored or only
   * proposed. Same rule as the RDV modal — see helpers/pendingChanges.
   */
  const [loadedColumnSignatures, setLoadedColumnSignatures] = useState<Map<string, string>>(
    new Map()
  );
  /** Rows that differ from what is stored — nothing is written until Save is pressed. */
  const pendingColumnChanges = useMemo(
    () =>
      pendingChanges(
        draftRows,
        loadedColumnSignatures,
        (row: DraftColumnRow) => row.id,
        draftRowSignature
      ),
    [draftRows, loadedColumnSignatures]
  );
  const pendingColumnCount = pendingColumnChanges.size;
  const [dmSourceRows, setDmSourceRows] = useState<DataMartSourceColumnRecord[]>([]);
  const [modalColumnSearchQuery, setModalColumnSearchQuery] = useState("");
  const [modalColumnViewFilter, setModalColumnViewFilter] =
    useState<ModalColumnViewFilter>("all");
  const [columnRowErrors, setColumnRowErrors] = useState<Record<string, string>>({});
  const [isColumnModalOpen, setIsColumnModalOpen] = useState(false);
  const [transformEditor, setTransformEditor] = useState<{
    mode: "draft" | "existing";
    rowId?: string;
    existingColumnId?: string;
    title: string;
    value: string;
    readOnly?: boolean;
  } | null>(null);
  const [descriptionEditor, setDescriptionEditor] = useState<{
    mode: "draft" | "existing";
    rowId?: string;
    existingColumnId?: string;
    title: string;
    value: string;
    readOnly?: boolean;
  } | null>(null);
  const [isPersistingColumns, setIsPersistingColumns] = useState(false);
  const [columnPersistError, setColumnPersistError] = useState<string | null>(null);
  const [isLoadingColumns, setIsLoadingColumns] = useState(false);
  const [columnLoadError, setColumnLoadError] = useState<string | null>(null);
  const [existingColumnEdits, setExistingColumnEdits] = useState<Record<string, ExistingColumnEditState>>({});
  const [dirtyExistingColumns, setDirtyExistingColumns] = useState<Record<string, boolean>>({});
  const [controlDrawerOpen, setControlDrawerOpen] = useState(false);
  const [isDeletingEntity, setIsDeletingEntity] = useState(false);
  const [deleteEntityError, setDeleteEntityError] = useState<string | null>(null);
  const [isAddingDataMart, setIsAddingDataMart] = useState(false);
  const [newDataMartName, setNewDataMartName] = useState("");
  const [dataMartCreateError, setDataMartCreateError] = useState<string | null>(null);
  const [isCreatingDataMart, setIsCreatingDataMart] = useState(false);
  const [sqlAutocomplete, setSqlAutocomplete] = useState<SqlAutocompleteContext>({
    sourceColumns: []
  });
  const headerChipClasses = isFactEntity
    ? "tw-bg-sky-100 tw-text-sky-700 tw-border-sky-200"
    : "tw-bg-emerald-100 tw-text-emerald-700 tw-border-emerald-200";
  const headerLabel = isFactEntity ? "Fact Table" : "Dimension";
  // Baseline is STATE (not a pure useMemo of the raw record) so that AUTO-RESOLUTION of
  // data_mart_id / target_entity_rdv_id / datamart (which happens after async option lists load —
  // e.g. when the column-mapping modal opens) can be applied to the baseline too. Those are
  // normalizations, not user edits, so syncing them into the baseline prevents a false "unsaved
  // changes" prompt on close. Only genuine user edits then move metadataDraft away from baseline.
  const [baselineMetadataDraft, setBaselineMetadataDraft] = useState<MetadataDraft>(() =>
    buildMetadataDraft(entity?.record, selectedEntityType)
  );
  const isMetadataDirty = useMemo(
    () => !areMetadataDraftsEqual(metadataDraft, baselineMetadataDraft),
    [baselineMetadataDraft, metadataDraft]
  );
  const isInternalPromotedEntity =
    entity.record.target_entity_dm_origin === "internal_registered" &&
    entity.record.source_entity_id != null;
  const hasAssignedRdvLineage = Boolean(metadataDraft.target_entity_rdv_id.trim());
  const generateModelEnabled = metadataDraft.generate_model_entity;
  const canToggleGenerateModel = !isFactEntity && !isInternalPromotedEntity;
  const generateToggleChecked = canToggleGenerateModel ? generateModelEnabled : false;
  const includeDqEnabled = metadataDraft.include_dq_test;
  const [showRdvLineageEditor, setShowRdvLineageEditor] = useState(false);
  const effectiveTargetEntityDmId = entity.record.target_entity_dm_id ?? resolvedTargetEntityDmId;
  const sourceNameByRdvColumnId = useMemo(() => {
    const entries = dmSourceRows
      .map((row) => {
        const rdvColumnId = row.target_entity_rdv_column_id;
        const sourceName =
          row.target_entity_rdv_column_name ??
          row.source_entity_column_name ??
          null;
        return rdvColumnId != null && sourceName ? [rdvColumnId, sourceName] as const : null;
      })
      .filter((entry): entry is readonly [number, string] => Boolean(entry));
    return new Map<number, string>(entries);
  }, [dmSourceRows]);
  const handleGenerateToggle = (enabled: boolean) =>
    setMetadataDraft((prev) => ({ ...prev, generate_model_entity: enabled }));
  const handleIncludeDqToggle = (enabled: boolean) =>
    setMetadataDraft((prev) => ({ ...prev, include_dq_test: enabled }));
  const renderControlActions = () => (
    <div className="tw-flex tw-w-full tw-flex-col tw-gap-2.5">
      <div className="tw-rounded-2xl tw-border tw-border-slate-200 tw-bg-slate-50 tw-p-3.5">
        <div className="tw-flex tw-items-center tw-justify-between tw-gap-3">
          <div className="tw-min-w-0">
            <p className="tw-text-[0.68rem] tw-font-semibold tw-uppercase tw-tracking-[0.28em] tw-text-slate-500">
              Generation
            </p>
            <p className="tw-mt-0.5 tw-text-sm tw-font-medium tw-text-slate-700">
              Generate model entity
            </p>
          </div>
          <button
            type="button"
            onClick={() => {
              if (canToggleGenerateModel) {
                handleGenerateToggle(!generateModelEnabled);
              }
            }}
            aria-pressed={generateToggleChecked}
            disabled={!canToggleGenerateModel}
            className={`tw-relative tw-inline-flex tw-h-7 tw-w-14 tw-shrink-0 tw-items-center tw-rounded-full tw-transition ${generateToggleChecked ? "tw-bg-[#03234a]" : "tw-bg-slate-300"
              } ${!canToggleGenerateModel ? "tw-cursor-not-allowed tw-opacity-50" : ""}`}
          >
            <span
              className={`tw-inline-block tw-h-6 tw-w-6 tw-transform tw-rounded-full tw-bg-white tw-shadow tw-transition ${generateToggleChecked ? "tw-translate-x-7" : "tw-translate-x-1"
                }`}
            />
          </button>
        </div>
        <p className="tw-mt-1.5 tw-pr-2 tw-text-xs tw-leading-5 tw-text-slate-500">
          {isInternalPromotedEntity
            ? "Promoted custom entities are catalog-backed nodes, so generated model output is unavailable."
            : isFactEntity
            ? "Fact model generation is not supported yet, so this setting is unavailable."
            : "Include this entity when generating downstream model artifacts."}
        </p>
      </div>
      <div className="tw-w-full">
        <button
          type="button"
          onClick={() => setControlDrawerOpen(true)}
          className="tw-inline-flex tw-items-center tw-justify-center tw-gap-2 tw-rounded-full tw-border tw-border-slate-300 tw-bg-white tw-px-4 tw-py-2 tw-text-sm tw-font-semibold tw-text-slate-700 tw-transition hover:tw-border-slate-400 hover:tw-bg-slate-50 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-offset-2 focus:tw:ring-slate-300"
        >
          Advanced settings
        </button>
      </div>
      <div className="tw-rounded-2xl tw-border tw-border-slate-100 tw-bg-white tw-p-5">
        <div className="tw-flex tw-flex-col tw-gap-3 sm:tw-flex-row sm:tw-items-center sm:tw-justify-between">
          <div className="tw-flex-1">
            <p className="tw-text-[0.68rem] tw-font-semibold tw-uppercase tw-tracking-[0.28em] tw-text-slate-500">
              Column mapping
            </p>
            <p className="tw-mt-1 tw-text-sm tw-font-medium tw-text-slate-700">
              {isInternalPromotedEntity && !hasAssignedRdvLineage
                ? "Review promoted columns for this entity"
                : "Map RDV columns to this entity"}
            </p>
            <p className="tw-mt-1 tw-text-xs tw-text-slate-500">
              {isInternalPromotedEntity && !hasAssignedRdvLineage
                ? entity.columns.length
                  ? `${entity.columns.length} promoted columns are available for review and editing.`
                  : "No promoted columns available yet."
                : entity.columns.length
                ? `${entity.columns.length} columns captured for this entity.`
                : "No columns mapped yet."}
            </p>
          </div>
          <button
            type="button"
            onClick={handleManageColumnsClick}
            disabled={isLoadingColumns || isSavingMetadata}
            className="tw-w-full tw-rounded-full tw-border tw-border-[#03234a] tw-bg-[#03234a] tw-px-4 tw-py-2 tw-text-sm tw-font-semibold tw-text-white tw-transition hover:tw-bg-[#021b39] hover:tw-border-[#021b39] focus:tw-outline-none focus:tw-ring-2 focus:tw:ring-offset-2 focus:tw:ring-[#03234a] disabled:tw-cursor-not-allowed disabled:tw-opacity-60 sm:tw-w-auto"
          >
            {isSavingMetadata ? "Saving..." : isLoadingColumns ? "Loading..." : "Open mapping"}
          </button>
        </div>
      </div>
    </div>
  );

  useEffect(() => {
    setResolvedTargetEntityDmId(entity?.record.target_entity_dm_id ?? null);
  }, [entity?.id, entity?.record.target_entity_dm_id]);

  useEffect(() => {
    setActiveTab("details");
    setDraftRows([]);
    setDmSourceRows([]);
    setModalColumnSearchQuery("");
    setModalColumnViewFilter("all");
    setColumnRowErrors({});
    setTransformEditor(null);
    setDescriptionEditor(null);
    setIsColumnModalOpen(false);
    setColumnLoadError(null);
    setIsLoadingColumns(false);
    setDeleteEntityError(null);
    setIsDeletingEntity(false);
    setIsAddingDataMart(false);
    setNewDataMartName("");
    setDataMartCreateError(null);
    setIsCreatingDataMart(false);
    setShowRdvLineageEditor(false);
    setValidationPopupMessage(null);
  }, [entity?.id]);

  // Re-initialize both draft and baseline from the record whenever the selected entity changes.
  useEffect(() => {
    if (entity) {
      const fresh = buildMetadataDraft(entity.record, selectedEntityType);
      setMetadataDraft(fresh);
      setBaselineMetadataDraft(fresh);
      setMetadataError(null);
      setValidationPopupMessage(null);
    }
  }, [entity, selectedEntityType]);

  useEffect(() => {
    if (hasAssignedRdvLineage) {
      setShowRdvLineageEditor(true);
    }
  }, [hasAssignedRdvLineage]);

  useEffect((): (() => void) | undefined => {
    const environmentIdRaw =
      typeof window !== "undefined" ? window.localStorage.getItem("environment_id") : null;
    const customerIdRaw =
      typeof window !== "undefined" ? window.localStorage.getItem("customer_id") : null;
    const environmentId = environmentIdRaw ? Number(environmentIdRaw) : null;
    const customerId = customerIdRaw ? Number(customerIdRaw) : null;
    const targetEntityRdvId = metadataDraft.target_entity_rdv_id
      ? Number(metadataDraft.target_entity_rdv_id)
      : null;
    const selectedRdvEntity = relatedRdvEntities.find(
      (option) => String(option.target_entity_rdv_id) === metadataDraft.target_entity_rdv_id
    );

    setSqlAutocomplete({
      sourceEntityName: selectedRdvEntity?.target_entity_rdv_name,
      targetEntityName: selectedRdvEntity?.target_entity_rdv_name,
      sourceColumns: [],
      targetColumns: []
    });

    if (!environmentId || !customerId || !targetEntityRdvId) {
      return undefined;
    }

    let isCancelled = false;

    fetchDataMartColumnAutocomplete(environmentId, customerId, targetEntityRdvId)
      .then((response) => {
        if (isCancelled) {
          return;
        }
        setSqlAutocomplete({
          sourceEntityName:
            response.target_entity_rdv_name ?? selectedRdvEntity?.target_entity_rdv_name,
          targetEntityName:
            response.target_entity_rdv_name ?? selectedRdvEntity?.target_entity_rdv_name,
          sourceColumns:
            response.target_entity_rdv_source_column_names ??
            response.target_entity_rdv_column_names ??
            [],
          targetColumns: response.target_entity_rdv_modeled_column_names ?? []
        });
      })
      .catch((error) => {
        console.error("Unable to load Data Mart SQL autocomplete metadata", error);
      });

    return () => {
      isCancelled = true;
    };
  }, [metadataDraft.target_entity_rdv_id, relatedRdvEntities]);

  useEffect(() => {
    if (selectedEntityType !== "fact") {
      return;
    }
    if (tableConstructValue?.trim()) {
      return;
    }
    const applyFactDefault = (previous: MetadataDraft): MetadataDraft =>
      previous.table_construct === FACT_TABLE_CONSTRUCT_DEFAULT
        ? previous
        : { ...previous, table_construct: FACT_TABLE_CONSTRUCT_DEFAULT };
    setMetadataDraft(applyFactDefault);
    setBaselineMetadataDraft(applyFactDefault);
  }, [selectedEntityType, tableConstructValue]);

  useEffect(() => {
    if (isDistributionKeyEditable) {
      return;
    }
    const clearDistributionKey = (previous: MetadataDraft): MetadataDraft =>
      previous.distribution_key ? { ...previous, distribution_key: "" } : previous;
    setMetadataDraft(clearDistributionKey);
    setBaselineMetadataDraft(clearDistributionKey);
  }, [isDistributionKeyEditable]);

  // Dimensions may source from any RDV construct the customer has actually built
  // (satellite, hub, or link — table or view — and legacy MANUAL entities). Previously
  // this was narrowed to satellites only, which HID hub/link/MANUAL-sourced dimensions and,
  // combined with the normalization effect below, silently WIPED their saved mapping on save.
  // Facts already allow every construct; dimensions now match that.
  const filteredRdvEntities = useMemo(
    () => relatedRdvEntities,
    [relatedRdvEntities]
  );

  const rdvSelectOptions = useMemo(() => {
    // Entities can share a name (a hub kept as both AsView and AsTable, migration twins,
    // or the same entity consolidated under two subject areas), so a bare name leaves the
    // modeller guessing. Qualify progressively: schema always, then construct if the name
    // repeats, then subject area if name AND construct both repeat.
    const countBy = (key: (option: TargetEntityRdvRecord) => string) =>
      filteredRdvEntities.reduce<Record<string, number>>((acc, option) => {
        const value = key(option);
        acc[value] = (acc[value] ?? 0) + 1;
        return acc;
      }, {});
    const nameKey = (option: TargetEntityRdvRecord) =>
      option.target_entity_rdv_name?.toLowerCase() ?? "";
    const nameConstructKey = (option: TargetEntityRdvRecord) =>
      `${nameKey(option)}|${(option.table_construct ?? "").toLowerCase()}`;
    const nameCounts = countBy(nameKey);
    const nameConstructCounts = countBy(nameConstructKey);

    return filteredRdvEntities.map((option) => {
      const name = option.target_entity_rdv_name;
      const schema = option.target_schema?.trim();
      const qualified = schema ? `${schema}.${name}` : name;

      const parts: string[] = [];
      // Customer-maintained objects are selectable sources too; say so, rather than
      // exposing the legacy 'MANUAL' construct value.
      if (isCustomRdvTableConstruct(option.table_construct)) {
        parts.push("custom");
      } else if ((nameCounts[nameKey(option)] ?? 0) > 1 && option.table_construct) {
        parts.push(option.table_construct);
      }
      if ((nameConstructCounts[nameConstructKey(option)] ?? 0) > 1 && option.subject_area) {
        parts.push(option.subject_area);
      }

      return {
        label: parts.length ? `${qualified} (${parts.join(", ")})` : qualified,
        value: String(option.target_entity_rdv_id),
        schema: schema ?? "",
        name: name ?? ""
      };
    })
      // Sort where the label is composed, not upstream: the list reads "schema.name", so
      // sorting on the name alone (which the service did) looks unsorted on screen.
      // Schema first, unqualified entries last, then name.
      .sort((left, right) => {
        if (Boolean(left.schema) !== Boolean(right.schema)) {
          return left.schema ? -1 : 1;
        }
        const bySchema = optionCollator.compare(left.schema, right.schema);
        return bySchema !== 0 ? bySchema : optionCollator.compare(left.name, right.name);
      })
      .map(({ label, value }) => ({ label, value }));
  }, [filteredRdvEntities]);

  const dataMartSelectOptions = useMemo(
    () =>
      dataMartOptions
        .map((option) => ({
          label: option.data_mart,
          value: String(option.data_mart_id)
        }))
        .sort((left, right) => optionCollator.compare(left.label ?? "", right.label ?? "")),
    [dataMartOptions]
  );

  useEffect(() => {
    if (!metadataDraft.target_entity_rdv_id) {
      return;
    }
    // Only clear the saved RDV id if it is genuinely absent from the FULL set of related
    // entities (e.g. the RDV entity was deleted) — never merely because it isn't a satellite.
    // Guarding against the unfiltered `relatedRdvEntities` (rather than a construct-narrowed
    // list) prevents silently wiping a valid hub/link/MANUAL-sourced dimension mapping on save.
    // While the list is still loading, do nothing so we don't clear a not-yet-loaded value.
    if (!relatedRdvEntities.length) {
      return;
    }
    const exists = relatedRdvEntities.some(
      (option) => String(option.target_entity_rdv_id) === metadataDraft.target_entity_rdv_id
    );
    if (!exists) {
      setMetadataDraft((previous) => ({ ...previous, target_entity_rdv_id: "" }));
      setBaselineMetadataDraft((previous) =>
        previous.target_entity_rdv_id ? { ...previous, target_entity_rdv_id: "" } : previous
      );
    }
  }, [relatedRdvEntities, metadataDraft.target_entity_rdv_id]);

  useEffect(() => {
    if (!entity || !dataMartOptions.length) {
      return;
    }
    // Resolve data_mart_id / datamart from the loaded option list. This is NORMALIZATION (the
    // entity record often has the datamart NAME but not its id) — so apply the SAME transform to
    // both the draft and the baseline, otherwise the resolved id makes the entity look edited.
    const resolveDataMart = (previous: MetadataDraft): MetadataDraft => {
      if (previous.data_mart_id) {
        const matchedById = dataMartOptions.find(
          (option) => String(option.data_mart_id) === previous.data_mart_id
        );
        if (!matchedById || previous.datamart === matchedById.data_mart) {
          return previous;
        }
        return { ...previous, datamart: matchedById.data_mart };
      }
      const fallbackName = (previous.datamart || entity.record.datamart || "").trim();
      if (!fallbackName) {
        return previous;
      }
      const matchedByName = dataMartOptions.find(
        (option) => option.data_mart.trim().toLowerCase() === fallbackName.toLowerCase()
      );
      if (!matchedByName) {
        return previous;
      }
      return {
        ...previous,
        data_mart_id: String(matchedByName.data_mart_id),
        datamart: matchedByName.data_mart
      };
    };
    setMetadataDraft(resolveDataMart);
    setBaselineMetadataDraft(resolveDataMart);
  }, [dataMartOptions, entity]);

  const getExistingColumnEdit = (column: DataMartFact["columns"][number]) => {
    const columnId = column.record.target_entity_dm_column_id;
    if (columnId == null) {
      return buildExistingColumnEditState(column);
    }
    return existingColumnEdits[String(columnId)] ?? buildExistingColumnEditState(column);
  };

  const hasDirtyExistingColumns = Object.keys(dirtyExistingColumns).length > 0;

  const loadColumnsForEntity = async (target: DataMartFact | DataMartDimension) => {
    const effectiveTargetRecord =
      target.record.target_entity_dm_id == null &&
        target.id === entity.id &&
        resolvedTargetEntityDmId != null
        ? { ...target.record, target_entity_dm_id: resolvedTargetEntityDmId }
        : target.record;
    if (!effectiveTargetRecord.target_entity_dm_id && !effectiveTargetRecord.target_entity_rdv_id) {
      return [];
    }
    const existingRecords = effectiveTargetRecord.target_entity_dm_id == null
      ? []
      : await fetchTargetEntityDmColumns(
        effectiveTargetRecord.environment_id,
        effectiveTargetRecord.customer_id
      );
    const filteredExistingRecords = existingRecords.filter(
      (record) => record.target_entity_dm_id === effectiveTargetRecord.target_entity_dm_id
    );
    // Load source columns for any dimension whose RDV source is set, regardless of construct
    // (hub / link / satellite / MANUAL). The backend returns the entity's own columns and,
    // for satellites, the inherited parent-hub keys. Previously this was gated to satellites
    // only, so hub/link/MANUAL-sourced dimensions never loaded their column mappings.
    const canLoadSourceColumns = isDimensionEntity;
    if (effectiveTargetRecord.target_entity_rdv_id && canLoadSourceColumns) {
      const sourceResponse = await fetchTargetEntityDmSourceColumns(
        effectiveTargetRecord.environment_id,
        effectiveTargetRecord.customer_id,
        effectiveTargetRecord.target_entity_rdv_id ?? null
      );
      setDmSourceRows(sourceResponse.items ?? []);
      const existingRdvColumnIds = new Set(
        filteredExistingRecords
          .map((record) => record.target_entity_rdv_column_id)
          .filter((value): value is number => value != null)
      );
      const hasPersistedDimensionKey = filteredExistingRecords.some((record) =>
        hasDimensionKeyValue(record.dimension_key)
      );
      // Also drop a source row whose column NAME already backs an existing column here.
      //
      // Matching on the RDV column id alone is not enough while the stored lineage points at
      // another entity's copy of the same column: `d_job_key` records `job_h_hash` as it
      // exists on the link, while the picker offers `job_h_hash` as it exists on the hub —
      // two ids, one physical column, so the row escaped the filter and was offered again
      // with Dim Key pre-set. Saving it would have added a SECOND dimension key. A dimension
      // reads from exactly one entity, so within this modal a repeated name is the same
      // column. Once the lineage is repointed onto the dimension's own source the ids agree
      // and this becomes redundant rather than wrong.
      const existingSourceColumnNames = new Set(
        filteredExistingRecords
          .map((record) =>
            (record.target_entity_rdv_column_name ?? "").trim().toLowerCase()
          )
          .filter((value) => value.length > 0)
      );
      const availableSourceRows = (sourceResponse.items ?? []).filter((record) => {
        const rdvColumnId = record.target_entity_rdv_column_id;
        if (rdvColumnId != null && existingRdvColumnIds.has(rdvColumnId)) {
          return false;
        }
        const sourceColumnName = (record.target_entity_rdv_column_name ?? "")
          .trim()
          .toLowerCase();
        return !(
          sourceColumnName && existingSourceColumnNames.has(sourceColumnName)
        );
      });
      const rdvDrafts = buildDimensionDraftRows(availableSourceRows, {
        entityId: target.id,
        dimensionName: effectiveTargetRecord.target_entity_dm_name ?? "",
        hasPersistedDimensionKey
      });
      setDraftRows(rdvDrafts);
      setLoadedColumnSignatures(
        new Map(rdvDrafts.map((row) => [row.id, draftRowSignature(row)]))
      );
      setColumnRowErrors({});
    } else {
      setDraftRows([]);
      setLoadedColumnSignatures(new Map());
      setDmSourceRows([]);
      setColumnRowErrors({});
    }
    if (effectiveTargetRecord.target_entity_dm_id == null) {
      return [];
    }
    return filteredExistingRecords;
  };

  const updateExistingColumnState = (
    column: DataMartFact["columns"][number],
    updater: (current: ExistingColumnEditState) => ExistingColumnEditState
  ) => {
    const columnId = column.record.target_entity_dm_column_id;
    if (columnId == null) {
      return;
    }
    const key = String(columnId);
    const baseState = existingColumnEdits[key] ?? buildExistingColumnEditState(column);
    const nextState = updater(baseState);
    setExistingColumnEdits((previous) => ({
      ...previous,
      [key]: nextState
    }));
    const isDirty = hasExistingColumnChanged(column, nextState);
    setDirtyExistingColumns((previous) => {
      const next = { ...previous };
      if (isDirty) {
        next[key] = true;
      } else {
        delete next[key];
      }
      return next;
    });
  };

  const handleExistingColumnInputChange = (
    column: DataMartFact["columns"][number],
    field: keyof Pick<
      ExistingColumnEditState,
      "columnName" | "reportViewColumnName" | "description" | "columnTransform"
    >,
    value: string
  ) => {
    updateExistingColumnState(column, (current) => ({
      ...current,
      [field]: value
    }));
  };

  const columnSignature = useMemo(() => {
    if (!entity) {
      return "";
    }
    return entity.columns
      .map((column) => {
        const id = column.record.target_entity_dm_column_id;
        const updatedAt = column.record.updated_at ?? "";
        const fallback = column.record.target_entity_dm_column_name;
        return `${id ?? fallback}-${updatedAt}`;
      })
      .join("|");
  }, [entity]);

  useEffect(() => {
    if (!entity) {
      setExistingColumnEdits({});
      setDirtyExistingColumns({});
      return;
    }
    const map: Record<string, ExistingColumnEditState> = {};
    entity.columns.forEach((column) => {
      const columnId = column.record.target_entity_dm_column_id;
      if (columnId != null) {
        map[String(columnId)] = buildExistingColumnEditState(column);
      }
    });
    setExistingColumnEdits(map);
    setDirtyExistingColumns({});
  }, [entity, columnSignature]);

  const filteredColumns = useMemo(() => {
    if (!entity) {
      return [];
    }
    return entity.columns.filter((column) => filterColumn(column, columnFilter));
  }, [entity]);

  const filteredExistingModalColumns = useMemo(() => {
    const normalizedSearch = modalColumnSearchQuery.trim().toLowerCase();
    return filteredColumns.filter((column) => {
      if (modalColumnViewFilter === "available" || modalColumnViewFilter === "inherited") {
        return false;
      }
      if (
        modalColumnViewFilter === "dim-key" &&
        !hasDimensionKeyValue(column.record.dimension_key)
      ) {
        return false;
      }
      if (!normalizedSearch) {
        return true;
      }
      const sourceName =
        column.record.target_entity_rdv_column_name ??
        (column.record.target_entity_rdv_column_id != null
          ? sourceNameByRdvColumnId.get(column.record.target_entity_rdv_column_id) ?? ""
          : "");
      const haystack = [
        sourceName,
        column.record.target_entity_dm_column_name,
        column.record.description
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(normalizedSearch);
    });
  }, [
    filteredColumns,
    modalColumnSearchQuery,
    modalColumnViewFilter,
    sourceNameByRdvColumnId
  ]);

  const filteredDraftModalRows = useMemo(() => {
    const normalizedSearch = modalColumnSearchQuery.trim().toLowerCase();
    return draftRows.filter((row) => {
      if (modalColumnViewFilter === "mapped") {
        return false;
      }
      if (modalColumnViewFilter === "inherited" && !row.isInherited) {
        return false;
      }
      if (modalColumnViewFilter === "dim-key" && !row.isDimensionKey) {
        return false;
      }
      if (!normalizedSearch) {
        return true;
      }
      const haystack = [row.sourceName, row.name, row.description]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(normalizedSearch);
    });
  }, [draftRows, modalColumnSearchQuery, modalColumnViewFilter]);

  const prioritizedExistingModalColumns = useMemo(
    () =>
      filteredExistingModalColumns.filter((column) =>
        hasDimensionKeyValue(column.record.dimension_key)
      ),
    [filteredExistingModalColumns]
  );

  const remainingExistingModalColumns = useMemo(
    () =>
      filteredExistingModalColumns.filter(
        (column) => !hasDimensionKeyValue(column.record.dimension_key)
      ),
    [filteredExistingModalColumns]
  );

  const prioritizedInheritedBkDraftRows = useMemo(
    () =>
      filteredDraftModalRows.filter(
        (row) => row.inheritanceType === "satellite_parent_hub_business_key"
      ),
    [filteredDraftModalRows]
  );

  const remainingDraftModalRows = useMemo(
    () =>
      filteredDraftModalRows.filter(
        (row) => row.inheritanceType !== "satellite_parent_hub_business_key"
      ),
    [filteredDraftModalRows]
  );

  if (!selectedEntity || !entity) {
    return null;
  }

  const toNullableString = (value: string): string | null => {
    const trimmed = value.trim();
    return trimmed ? trimmed : null;
  };

  const persistMetadata = async () => {
    if (!entity) {
      return;
    }
    if (!metadataDraft.target_entity_dm_name.trim()) {
      setMetadataError("Target entity name is required.");
      return;
    }
    if (!metadataDraft.data_mart_id.trim()) {
      setMetadataError("Data mart selection is required.");
      return;
    }
    const normalizedName = metadataDraft.target_entity_dm_name.trim();
    const selectedDataMart = dataMartOptions.find(
      (option) => String(option.data_mart_id) === metadataDraft.data_mart_id.trim()
    );
    if (!selectedDataMart) {
      setMetadataError("Please select a valid data mart.");
      return;
    }
    const distributionKeyError = showDistributionControls
      ? getDistributionValidationError(
          normalizedDistributionType,
          metadataDraft.distribution_key
        )
      : null;
    if (distributionKeyError) {
      setMetadataError(distributionKeyError);
      return;
    }
    const distributionTypeValue = normalizedDistributionType ? normalizedDistributionType : null;
    const selectedRdvId = metadataDraft.target_entity_rdv_id.trim()
      ? Number(metadataDraft.target_entity_rdv_id)
      : null;
    if (
      isDimensionEntity &&
      entity.record.target_entity_dm_id == null &&
      selectedRdvId == null
    ) {
      setValidationPopupMessage(
        "RDV Entity Select is required before saving a new dimension."
      );
      return;
    }

    const payload: Partial<DataMartFact["record"]> = {
      ...entity.record,
      target_entity_dm_name: normalizedName,
      target_entity_dm_report_view_name:
        metadataDraft.target_entity_dm_report_view_name.trim() || normalizedName,
      datamart: selectedDataMart.data_mart,
      data_mart_id: selectedDataMart.data_mart_id,
      subject_area: null,
      table_construct: isFactEntity
        ? FACT_TABLE_CONSTRUCT_DEFAULT
        : toNullableString(metadataDraft.table_construct),
      distribution_type: distributionTypeValue,
      distribution_key: isDistributionKeyEditable
        ? toNullableString(metadataDraft.distribution_key)
        : null,
      target_entity_rdv_id: selectedRdvId,
      description: toNullableString(metadataDraft.description),
      target_schema: toNullableString(metadataDraft.target_schema),
      target_entity_dm_report_view_source_filter: toNullableString(
        metadataDraft.target_entity_dm_report_view_source_filter
      ),
      target_entity_rdv_source_filter: toNullableString(
        metadataDraft.target_entity_rdv_source_filter
      ),
      dq_source_filter: toNullableString(metadataDraft.dq_source_filter),
      generate_model_entity: metadataDraft.generate_model_entity,
      include_dq_test: metadataDraft.include_dq_test,
      include_start_record: metadataDraft.include_start_record ? true : false,
      use_second_template_version: metadataDraft.use_second_template_version
    };

    try {
      setIsSavingMetadata(true);
      const savedRecords = await saveTargetEntityMetadata([payload]);
      const savedRecord = savedRecords[0] ?? null;
      const nextRecord = savedRecord
        ? { ...entity.record, ...payload, ...savedRecord }
        : { ...entity.record, ...payload };
      setResolvedTargetEntityDmId(nextRecord.target_entity_dm_id ?? null);
      onUpdateEntity(entity.id, nextRecord);
      setMetadataDraft(buildMetadataDraft(nextRecord, selectedEntityType));
      setMetadataError(null);
    } catch (error) {
      console.error("Unable to save target entity metadata", error);
      const message =
        error instanceof Error ? error.message : "Unable to save Data Mart details.";
      setMetadataError(message);
    } finally {
      setIsSavingMetadata(false);
    }
  };

  const handleSaveMetadata = async (event: React.FormEvent) => {
    event.preventDefault();
    await persistMetadata();
  };

  const discardSessionChanges = () => {
    setMetadataDraft(baselineMetadataDraft);
    setMetadataError(null);
    setValidationPopupMessage(null);
    setDraftRows([]);
    setDmSourceRows([]);
    setModalColumnSearchQuery("");
    setModalColumnViewFilter("all");
    setColumnRowErrors({});
    setTransformEditor(null);
    setDescriptionEditor(null);
    setColumnPersistError(null);
    setColumnLoadError(null);
    setIsColumnModalOpen(false);
    const resetExistingEdits: Record<string, ExistingColumnEditState> = {};
    entity.columns.forEach((column) => {
      const columnId = column.record.target_entity_dm_column_id;
      if (columnId != null) {
        resetExistingEdits[String(columnId)] = buildExistingColumnEditState(column);
      }
    });
    setExistingColumnEdits(resetExistingEdits);
    setDirtyExistingColumns({});
  };

  const hasUnsavedBladeChanges =
    isMetadataDirty || draftRows.length > 0 || hasDirtyExistingColumns;

  const handleClosePanel = () => {
    if (hasUnsavedBladeChanges) {
      const shouldDiscard = window.confirm(
        "Discard unsaved changes and close this details panel?"
      );
      if (!shouldDiscard) {
        return;
      }
      discardSessionChanges();
    }
    onClose();
  };

  const handleCreateDataMart = async () => {
    setDataMartCreateError(null);
    setIsCreatingDataMart(true);
    const result = await onCreateDataMart(newDataMartName);
    setIsCreatingDataMart(false);
    if (!result.ok || !result.option) {
      setDataMartCreateError(result.error ?? "Unable to create Data Mart.");
      return;
    }
    setMetadataDraft((prev) => ({
      ...prev,
      data_mart_id: String(result.option?.data_mart_id ?? ""),
      datamart: result.option?.data_mart ?? prev.datamart
    }));
    setIsAddingDataMart(false);
    setNewDataMartName("");
  };

  const updateDraftRow = (
    rowId: string,
    updates: Partial<Omit<DraftColumnRow, "id" | "entityId">>
  ) => {
    setDraftRows((previous) =>
      previous.map((row) => (row.id === rowId ? { ...row, ...updates } : row))
    );
    setColumnRowErrors((previous) => {
      if (!previous[rowId]) {
        return previous;
      }
      const next = { ...previous };
      delete next[rowId];
      return next;
    });
  };

  const handleDraftToggle = (
    rowId: string,
    field: keyof Pick<DraftColumnRow, "isDimensionKey" | "isDimensionAttribute" | "isPii">,
    nextValue?: boolean
  ) => {
    setDraftRows((previous) =>
      previous.map((row) => {
        if (row.id !== rowId) {
          return row;
        }
        if (field === "isDimensionAttribute" && row.isDimensionKey) {
          return row;
        }
        const toggledValue = typeof nextValue === "boolean" ? nextValue : !row[field];
        const updated: DraftColumnRow = {
          ...row,
          [field]: toggledValue
        };
        if (field === "isDimensionKey" && toggledValue) {
          updated.isDimensionAttribute = true;
        }
        return updated;
      })
    );
  };

  const handleAddManualFactDraftRow = () => {
    if (!entity || !isFactEntity) {
      return;
    }
    const nextId =
      typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
        ? crypto.randomUUID()
        : `manual-${Date.now()}`;
    setDraftRows((previous) => [
      ...previous,
      {
        id: nextId,
        entityId: entity.id,
        sourceName: "Manual",
        name: "",
        reportViewName: "",
        description: "",
        columnTransform: "",
        isDimensionKey: false,
        isDimensionAttribute: false,
        isPii: false,
        target_entity_rdv_column_id: null,
        target_entity_dm_column_id: null,
        isInherited: false,
        inheritanceType: null,
        inheritedFromName: null
      }
    ]);
  };

  const handlePersistColumns = async () => {
    if (!entity || !effectiveTargetEntityDmId) {
      setColumnPersistError("Save the target entity before modifying columns.");
      return;
    }
    let hasErrors = false;
    const nextErrors: Record<string, string> = {};
    const createPayloads: DataMartColumnCreatePayload[] = [];
    draftRows.forEach((row) => {
      const trimmedName = row.name.trim();
      if (!trimmedName) {
        hasErrors = true;
        nextErrors[row.id] = "Target column name is required.";
        return;
      }
      if (row.columnTransform.trim() && row.target_entity_rdv_column_id == null) {
        hasErrors = true;
        nextErrors[row.id] = "Derivation transform requires an upstream RDV column.";
        return;
      }
      const sanitizedRow: CreateDataMartColumnInput = {
        entityId: row.entityId,
        sourceName: row.sourceName,
        name: trimmedName,
        reportViewName: trimmedName,
        description: row.description.trim(),
        columnTransform: row.columnTransform,
        isDimensionKey: row.isDimensionKey,
        isDimensionAttribute: row.isDimensionAttribute,
        isPii: row.isPii,
        target_entity_rdv_column_id: row.target_entity_rdv_column_id ?? null,
        target_entity_dm_column_id: row.target_entity_dm_column_id ?? null
      };
      createPayloads.push(buildColumnCreatePayload(sanitizedRow, effectiveTargetEntityDmId));
    });
    if (hasErrors) {
      setColumnRowErrors((previous) => ({ ...previous, ...nextErrors }));
      setColumnPersistError("Resolve validation errors before saving.");
      return;
    }
    const updatePayloads: DataMartColumnUpdatePayload[] = [];
    entity.columns.forEach((column) => {
      const columnId = column.record.target_entity_dm_column_id;
      if (columnId == null || !dirtyExistingColumns[String(columnId)]) {
        return;
      }
      const edits = existingColumnEdits[String(columnId)] ?? buildExistingColumnEditState(column);
      if (!hasExistingColumnChanged(column, edits)) {
        return;
      }
      if (edits.columnTransform.trim() && column.record.target_entity_rdv_column_id == null) {
        hasErrors = true;
        setColumnPersistError(
          "Derivation transforms require upstream RDV column lineage on every edited column."
        );
        return;
      }
      const payload = buildUpdatePayloadFromColumn(column, edits);
      if (payload) {
        updatePayloads.push(payload);
      }
    });
    if (hasErrors) {
      return;
    }
    if (!createPayloads.length && !updatePayloads.length) {
      setColumnPersistError("No column changes to save.");
      return;
    }
    try {
      setIsPersistingColumns(true);
      setColumnPersistError(null);
      if (createPayloads.length) {
        await createTargetEntityDmColumns(
          createPayloads,
          entity.record.environment_id,
          entity.record.customer_id
        );
      }
      if (updatePayloads.length) {
        await updateTargetEntityDmColumns(
          updatePayloads,
          entity.record.environment_id,
          entity.record.customer_id
        );
      }
      const refreshedRecords = await loadColumnsForEntity(entity);
      onSyncColumns(entity.id, refreshedRecords);
      setDraftRows([]);
      setColumnRowErrors({});
      setExistingColumnEdits({});
      setDirtyExistingColumns({});
    } catch (error) {
      console.error("Unable to save Data Mart columns", error);
      const message =
        error instanceof Error ? error.message : "Unable to save Data Mart columns.";
      setColumnPersistError(message);
    } finally {
      setIsPersistingColumns(false);
    }
  };

  const handleExistingColumnToggle = (
    column: DataMartFact["columns"][number],
    field: keyof Pick<ExistingColumnEditState, "dimensionKey" | "dimensionAttribute" | "pii">,
    nextValue: boolean
  ) => {
    updateExistingColumnState(column, (current) => {
      if (field === "dimensionAttribute" && current.dimensionKey) {
        return current;
      }
      if (field === "dimensionKey") {
        return {
          ...current,
          dimensionKey: nextValue,
          dimensionAttribute: nextValue ? true : current.dimensionAttribute
        };
      }
      return {
        ...current,
        [field]: nextValue
      };
    });
  };

  const buildUpdatePayloadFromColumn = (
    column: DataMartFact["columns"][number],
    edits: ExistingColumnEditState
  ): DataMartColumnUpdatePayload | null => {
    const columnId = column.record.target_entity_dm_column_id;
    const targetId = column.record.target_entity_dm_id ?? entity?.record.target_entity_dm_id ?? null;
    if (columnId == null || targetId == null) {
      return null;
    }
    const normalizedName = edits.columnName.trim() || column.record.target_entity_dm_column_name;
    const derivedRole = deriveColumnRoleFromFlags(edits.dimensionKey, edits.dimensionAttribute);
    const hasRdvLineage = column.record.target_entity_rdv_column_id != null;
    const payload: DataMartColumnUpdatePayload = {
      target_entity_dm_column_id: columnId,
      target_entity_dm_id: targetId,
      target_entity_dm_column_name: normalizedName,
      target_entity_dm_report_view_column_name: normalizedName,
      description: edits.description.trim() || null,
      dimension_key: edits.dimensionKey ? true : null,
      dimension_attribute: buildDimensionAttributeValue(derivedRole),
      pii: edits.pii,
      is_active: column.record.is_active ?? true,
      target_entity_rdv_column_id: column.record.target_entity_rdv_column_id ?? null
    };
    if (hasRdvLineage) {
      payload.target_entity_dm_column_transform = edits.columnTransform.trim() || null;
      payload.target_entity_dm_report_view_column_transform =
        column.record.target_entity_dm_report_view_column_transform ?? null;
    }
    return payload;
  };

  const openColumnModal = () => {
    if (!entity) {
      return;
    }
    setIsColumnModalOpen(true);
  };

  const handleManageColumnsClick = async () => {
    if (!entity) {
      return;
    }
    if (!effectiveTargetEntityDmId) {
      openColumnModal();
      return;
    }
    setIsLoadingColumns(true);
    setColumnLoadError(null);
    try {
      const filteredRecords = await loadColumnsForEntity(entity);
      onSyncColumns(entity.id, filteredRecords);
    } catch (error) {
      console.error("Unable to load Data Mart columns", error);
      const message =
        error instanceof Error ? error.message : "Unable to load Data Mart columns.";
      setColumnLoadError(message);
    } finally {
      setIsLoadingColumns(false);
      openColumnModal();
    }
  };

  const closeColumnModal = () => {
    setIsColumnModalOpen(false);
    // Opening the modal auto-loads "available" RDV source columns into draftRows as SUGGESTIONS.
    // Closing without saving must discard them — otherwise draftRows.length stays > 0 and the
    // details panel falsely reports unsaved changes (spurious "Discard changes?" on close).
    setDraftRows([]);
    setColumnRowErrors({});
    setDirtyExistingColumns({});
    setExistingColumnEdits((previous) => {
      // reset any in-modal existing-column edits back to their saved state
      const reset: Record<string, ExistingColumnEditState> = {};
      entity?.columns.forEach((column) => {
        const columnId = column.record.target_entity_dm_column_id;
        if (columnId != null) {
          reset[String(columnId)] = buildExistingColumnEditState(column);
        }
      });
      void previous;
      return reset;
    });
  };

  const openDraftTransformEditor = (row: DraftColumnRow) => {
    const label = "Derivation transform";
    const titleName = row.reportViewName.trim() || row.name.trim() || "New column";
    setTransformEditor({
      mode: "draft",
      rowId: row.id,
      title: `${label} · ${titleName}`,
      value: row.columnTransform
    });
  };

  const openExistingTransformEditor = (column: DataMartFact["columns"][number]) => {
    const columnId = column.record.target_entity_dm_column_id;
    if (columnId == null) {
      return;
    }
    const label = "Derivation transform";
    const editState = getExistingColumnEdit(column);
    setTransformEditor({
      mode: "existing",
      existingColumnId: String(columnId),
      title: `${label} · ${column.record.target_entity_dm_report_view_column_name ?? column.record.target_entity_dm_column_name}`,
      value: editState.columnTransform
    });
  };

  const handleTransformEditorChange = (value: string) => {
    setTransformEditor((previous) => {
      if (!previous || previous.readOnly) {
        return previous;
      }
      return { ...previous, value };
    });
  };

  const handleTransformEditorSave = () => {
    if (!transformEditor) {
      return;
    }
    if (transformEditor.mode === "draft") {
      if (!transformEditor.rowId) {
        setTransformEditor(null);
        return;
      }
      updateDraftRow(transformEditor.rowId, { columnTransform: transformEditor.value });
      setTransformEditor(null);
      return;
    }
    if (transformEditor.mode === "existing" && transformEditor.existingColumnId && entity) {
      const column = entity.columns.find(
        (candidate) =>
          candidate.record.target_entity_dm_column_id != null &&
          String(candidate.record.target_entity_dm_column_id) === transformEditor.existingColumnId
      );
      if (column) {
        updateExistingColumnState(column, (current) => ({
          ...current,
          columnTransform: transformEditor.value
        }));
      }
      setTransformEditor(null);
      return;
    }
    setTransformEditor(null);
  };

  const openDraftDescriptionEditor = (row: DraftColumnRow) => {
    const titleName = row.name.trim() || "New column";
    setDescriptionEditor({
      mode: "draft",
      rowId: row.id,
      title: `Description · ${titleName}`,
      value: row.description
    });
  };

  const openExistingDescriptionEditor = (column: DataMartFact["columns"][number]) => {
    const columnId = column.record.target_entity_dm_column_id;
    const editState = getExistingColumnEdit(column);
    setDescriptionEditor({
      mode: "existing",
      existingColumnId: columnId != null ? String(columnId) : undefined,
      title: `Description · ${column.record.target_entity_dm_column_name}`,
      value: editState.description,
      readOnly: columnId == null
    });
  };

  const handleDescriptionEditorChange = (value: string) => {
    setDescriptionEditor((previous) => {
      if (!previous || previous.readOnly) {
        return previous;
      }
      return { ...previous, value };
    });
  };

  const handleDescriptionEditorSave = () => {
    if (!descriptionEditor) {
      return;
    }
    if (descriptionEditor.mode === "draft" && descriptionEditor.rowId) {
      updateDraftRow(descriptionEditor.rowId, { description: descriptionEditor.value });
      setDescriptionEditor(null);
      return;
    }
    if (
      descriptionEditor.mode === "existing" &&
      descriptionEditor.existingColumnId &&
      entity
    ) {
      const column = entity.columns.find(
        (candidate) =>
          candidate.record.target_entity_dm_column_id != null &&
          String(candidate.record.target_entity_dm_column_id) === descriptionEditor.existingColumnId
      );
      if (column) {
        handleExistingColumnInputChange(column, "description", descriptionEditor.value);
      }
    }
    setDescriptionEditor(null);
  };

  const handleTransformEditorCancel = () => {
    setTransformEditor(null);
  };

  const handleDeleteEntity = async () => {
    const confirmed = window.confirm(
      "Delete this Data Mart entity? This will remove related relationship lines."
    );
    if (!confirmed) {
      return;
    }
    setDeleteEntityError(null);
    setIsDeletingEntity(true);
    try {
      await onDeleteEntity(
        entity.id,
        selectedEntityType,
        entity.record.target_entity_dm_id ?? null
      );
      setControlDrawerOpen(false);
      onClose();
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Unable to delete Data Mart entity.";
      setDeleteEntityError(message);
    } finally {
      setIsDeletingEntity(false);
    }
  };

  const renderRelationships = () => {
    const modelFacts = currentGraph?.facts ?? [];
    if (factEntity && factEntity.dimensionIds.length) {
      return (
        <div className="tw-space-y-2">
          {factEntity.dimensionIds.map((dimensionId: string) => (
            <div
              key={dimensionId}
              className="tw-flex tw-items-center tw-justify-between tw-rounded-xl tw-border tw-border-slate-200 tw-bg-white tw-p-3"
            >
              <div>
                <p className="tw-text-sm tw-font-semibold tw-text-slate-800">
                  {dimensionId}
                </p>
                <p className="tw-text-xs tw-text-slate-500">
                  Dimension relationship
                </p>
              </div>
              <div className="tw-flex tw-items-center tw-gap-2">
                {onRemoveRelationship && (
                  <button
                    type="button"
                    className="tw-rounded-full tw-border tw-border-rose-200 tw-bg-rose-50 tw-px-2.5 tw-py-1 tw-text-[10px] tw-font-semibold tw-uppercase tw-text-rose-600 hover:tw-bg-rose-100"
                    onClick={() => onRemoveRelationship(factEntity.id, dimensionId)}
                  >
                    Remove
                  </button>
                )}
                <FiExternalLink className="tw-h-4 tw-w-4 tw-text-slate-400" />
              </div>
            </div>
          ))}
        </div>
      );
    }
    if (dimensionEntity) {
      const connectedFacts = modelFacts
        .filter((fact: DataMartFact) => fact.dimensionIds.includes(dimensionEntity.id))
        .map((fact: DataMartFact) => fact.record.target_entity_dm_name ?? fact.id);
      return (
        <div className="tw-space-y-4">
          {connectedFacts.length > 0 ? (
            <div>
              <p className="tw-text-[11px] tw-font-semibold tw-uppercase tw-text-slate-500">
                Connected Facts
              </p>
              <ul className="tw-mt-2 tw-space-y-1 tw-text-sm tw-text-slate-700">
                {connectedFacts.map((factName: string) => (
                  <li key={factName} className="tw-flex tw-items-center tw-gap-2">
                    <FiLink className="tw-h-3 tw-w-3 tw-text-emerald-600" />
                    {factName}
                  </li>
                ))}
              </ul>
            </div>
          ) : (
            <p className="tw-text-sm tw-text-slate-500">
              No fact entities linked to this dimension yet.
            </p>
          )}
          {dimensionEntity.parentDimensionIds.length > 0 && (
            <div>
              <p className="tw-text-[11px] tw-font-semibold tw-uppercase tw-text-slate-500">
                Parents
              </p>
              <ul className="tw-mt-2 tw-space-y-1 tw-text-sm tw-text-slate-700">
                {dimensionEntity.parentDimensionIds.map((parentId: string) => (
                  <li key={parentId} className="tw-flex tw-items-center tw-gap-2">
                    <FiLink className="tw-h-3 tw-w-3 tw-text-emerald-600" />
                    {parentId}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {dimensionEntity.childDimensionIds.length > 0 && (
            <div>
              <p className="tw-text-[11px] tw-font-semibold tw-uppercase tw-text-slate-500">
                Snowflake Children
              </p>
              <ul className="tw-mt-2 tw-space-y-1 tw-text-sm tw-text-slate-700">
                {dimensionEntity.childDimensionIds.map((childId: string) => (
                  <li key={childId} className="tw-flex tw-items-center tw-gap-2">
                    <FiLink className="tw-h-3 tw-w-3 tw-text-emerald-600" />
                    {childId}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      );
    }
    return (
      <p className="tw-text-sm tw-text-slate-500">No relationships have been inferred yet.</p>
    );
  };

  const renderDraftRow = (row: DraftColumnRow) => {
    const draftOrigin = getDraftOrigin(row);
    const pendingKind = pendingColumnChanges.get(row.id);
    return (
    <tr
      key={row.id}
      // A pending row carries an amber left edge, so unsaved work is scannable down the
      // grid — the same language as the RDV modal.
      className={`tw-border-b tw-border-slate-100 ${
        pendingKind ? "tw-border-l-4 tw-border-l-amber-400 tw-bg-amber-50/40" : "tw-bg-white"
      }`}
      // No extra cell: adding one would shift every column against the header. The edge
      // colour carries it, and the title makes it readable.
      title={
        pendingKind === "new"
          ? "Not saved yet"
          : pendingKind === "changed"
          ? "Edited since loading — not saved yet"
          : undefined
      }
    >

      <td className="tw-w-48 tw-align-middle tw-px-3 tw-py-3">
        <div className="tw-flex tw-min-h-[3.5rem] tw-flex-col tw-justify-center">
          <input
            type="text"
            value={row.sourceName}
            readOnly
            className={`${tableInputClass} tw-cursor-default tw-bg-slate-50 tw-text-slate-600`}
            placeholder="Source column"
            title={row.sourceName}
          />
          {columnRowErrors[row.id] && (
            <p className="tw-mt-1 tw-text-[11px] tw-font-semibold tw-leading-4 tw-text-rose-600">
              {columnRowErrors[row.id]}
            </p>
          )}
        </div>
      </td>
      <td className="tw-w-32 tw-align-middle tw-px-3 tw-py-3 tw-text-center">
        <div className="tw-flex tw-min-h-[3.5rem] tw-items-center tw-justify-center">
          <span
            title={draftOrigin.detail}
            className={`tw-rounded-full tw-px-2.5 tw-py-1 tw-text-[10px] tw-font-semibold tw-uppercase tw-tracking-wide ${draftOrigin.tone}`}
          >
            {draftOrigin.label}
          </span>
        </div>
      </td>
      <td className="tw-w-48 tw-align-middle tw-px-3 tw-py-3 tw-text-center">
        <input
          type="text"
          value={row.name}
          onChange={(event) =>
            updateDraftRow(row.id, { name: event.target.value })
          }
          className={tableInputClass}
          placeholder="Target column (required)"
        />
      </td>
      <td className="tw-w-32 tw-align-middle tw-px-3 tw-py-3 tw-text-center">
        <ToggleControl
          label=""
          checked={row.isDimensionKey}
          onChange={(checked) => handleDraftToggle(row.id, "isDimensionKey", checked)}
          disabled={isFactEntity && row.target_entity_rdv_column_id == null && !row.isInherited}
          compact
        />
      </td>
      <td className="tw-w-32 tw-align-middle tw-px-3 tw-py-3 tw-text-center">
        <ToggleControl
          label=""
          checked={row.isDimensionAttribute}
          onChange={(checked) => handleDraftToggle(row.id, "isDimensionAttribute", checked)}
          disabled={row.isDimensionKey || (isFactEntity && row.target_entity_rdv_column_id == null && !row.isInherited)}
          compact
        />
      </td>
      <td className="tw-w-32 tw-align-middle tw-px-3 tw-py-3 tw-text-center">
        <ToggleControl
          label=""
          checked={row.isPii}
          onChange={(checked) => handleDraftToggle(row.id, "isPii", checked)}
          compact
        />
      </td>
      <td className="tw-w-[12rem] tw-max-w-[12rem] tw-align-middle tw-px-3 tw-py-3 tw-text-center">
        <button
          type="button"
          onClick={() => openDraftDescriptionEditor(row)}
          className="tw-flex tw-h-12 tw-w-full tw-items-center tw-rounded-2xl tw-border tw-border-dashed tw-border-slate-200 tw-bg-slate-50 tw-p-3 tw-text-left hover:tw-border-[#03234a]"
        >
          <span className="tw-block tw-w-full tw-text-[11px] tw-text-slate-600 tw-truncate">
            {row.description?.trim() ? row.description : "Add description"}
          </span>
        </button>
      </td>
      <td className="tw-w-[18rem] tw-max-w-[18rem] tw-align-middle tw-px-3 tw-py-3 tw-text-center">
        <button
          type="button"
          onClick={() => openDraftTransformEditor(row)}
          className="tw-flex tw-h-12 tw-w-full tw-items-center tw-rounded-2xl tw-border tw-border-dashed tw-border-slate-200 tw-bg-slate-50 tw-p-3 tw-text-left hover:tw-border-[#03234a]"
        >
          <div className="tw-block tw-w-full">
            <SqlPreview value={row.columnTransform} placeholder="Add transformation" />
          </div>
        </button>
      </td>
    </tr>
  );
  };

  const renderExistingRow = (column: DataMartFact["columns"][number]) => {
    const columnId = column.record.target_entity_dm_column_id;
    const editState = getExistingColumnEdit(column);
    const columnEditable = columnId != null;
    const hasRdvLineage = column.record.target_entity_rdv_column_id != null;
    const isRelationshipGenerated = column.record.column_origin === "relationship_generated";
    const isManualFactColumn =
      isFactEntity &&
      column.record.column_origin === "manual" &&
      !hasDimensionKeyValue(column.record.dimension_key);
    const disableInputs = !columnEditable || isPersistingColumns;
    const resolvedSourceName =
      column.record.target_entity_rdv_column_name ??
      (column.record.target_entity_rdv_column_id != null
        ? sourceNameByRdvColumnId.get(column.record.target_entity_rdv_column_id) ?? ""
        : "");
    const fallbackSourceName =
      resolvedSourceName ||
      column.record.target_entity_dm_report_view_column_name ||
      column.record.target_entity_dm_column_name ||
      (isInternalPromotedEntity && !hasAssignedRdvLineage ? "Promoted from catalog" : "");
    return (
      <tr
        key={columnId ?? column.record.target_entity_dm_column_name}
        className="tw-border-b tw-border-slate-100 tw-bg-slate-50"
      >
        <td className="tw-w-48 tw-align-middle tw-px-3 tw-py-3 tw-text-center">
          <div className="tw-flex tw-min-h-[3.5rem] tw-items-center">
            <input
              type="text"
              value={fallbackSourceName}
              readOnly
              disabled
              className={`${tableInputClass} tw-cursor-default tw-bg-slate-100 tw-text-slate-600`}
              placeholder="Source column"
              title={fallbackSourceName}
            />
          </div>
        </td>
        <td className="tw-w-32 tw-align-middle tw-px-3 tw-py-3 tw-text-center">
          <div className="tw-flex tw-min-h-[3.5rem] tw-items-center tw-justify-center">
            {getColumnOriginLabel(column.record.column_origin) ? (
              <span
                title={
                  isRelationshipGenerated
                    ? "Derived from dimension relationship"
                    : undefined
                }
                className={`tw-rounded-full tw-px-2.5 tw-py-1 tw-text-[10px] tw-font-semibold tw-uppercase tw-tracking-wide ${getColumnOriginBadgeClass(column.record.column_origin)}`}
              >
                {getColumnOriginLabel(column.record.column_origin)}
              </span>
            ) : (
              <span className="tw-text-xs tw-text-slate-400">-</span>
            )}
          </div>
        </td>
        <td className="tw-w-48 tw-align-middle tw-px-3 tw-py-3 tw-text-center">
          <input
            type="text"
            value={editState.columnName}
            onChange={(event) =>
              handleExistingColumnInputChange(
                column,
                "columnName",
                event.target.value
              )
            }
            disabled={disableInputs || isRelationshipGenerated}
            className={`${tableInputClass} ${disableInputs || isRelationshipGenerated ? "tw-bg-slate-100" : ""}`}
            placeholder="Target column (required)"
          />
        </td>
        <td className="tw-w-32 tw-align-middle tw-px-3 tw-py-3 tw-text-center">
          <ToggleControl
            label=""
            checked={editState.dimensionKey}
            onChange={(checked) => handleExistingColumnToggle(column, "dimensionKey", checked)}
            disabled={disableInputs || isRelationshipGenerated || isManualFactColumn}
            compact
          />
        </td>
        <td className="tw-w-32 tw-align-middle tw-px-3 tw-py-3 tw-text-center">
          <ToggleControl
            label=""
            checked={editState.dimensionAttribute}
            onChange={(checked) =>
              handleExistingColumnToggle(column, "dimensionAttribute", checked)
            }
            disabled={disableInputs || editState.dimensionKey || isRelationshipGenerated || isManualFactColumn}
            compact
          />
        </td>
        <td className="tw-w-32 tw-align-middle tw-px-3 tw-py-3 tw-text-center">
          <ToggleControl
            label=""
            checked={editState.pii}
            onChange={(checked) => handleExistingColumnToggle(column, "pii", checked)}
            disabled={disableInputs}
            compact
          />
        </td>
        <td className="tw-w-[12rem] tw-max-w-[12rem] tw-align-middle tw-px-3 tw-py-3 tw-text-center">
          <button
            type="button"
            onClick={() => openExistingDescriptionEditor(column)}
            disabled={disableInputs}
            className={`tw-flex tw-h-12 tw-w-full tw-items-center tw-rounded-2xl tw-border tw-p-3 tw-text-left ${disableInputs
              ? "tw-border-slate-200 tw-bg-slate-100 tw-text-slate-400"
              : editState.description?.trim()
                ? "tw-border-slate-200 tw-bg-white"
                : "tw-border-dashed tw-border-slate-200 tw-bg-slate-50"
              } hover:tw-border-[#03234a]`}
          >
            <span className="tw-block tw-w-full tw-text-[11px] tw-text-slate-600 tw-truncate">
              {editState.description?.trim() ? editState.description : "Add description"}
            </span>
          </button>
        </td>
        <td className="tw-w-[18rem] tw-max-w-[18rem] tw-align-middle tw-px-3 tw-py-3 tw-text-center">
          <button
            type="button"
            onClick={() => openExistingTransformEditor(column)}
            disabled={!columnEditable || !hasRdvLineage}
            className={`tw-flex tw-h-12 tw-w-full tw-items-center tw-rounded-2xl tw-border tw-p-3 tw-text-left ${!columnEditable || !hasRdvLineage
              ? "tw-border-slate-200 tw-bg-slate-100 tw-text-slate-400"
              : editState.columnTransform?.trim()
                ? "tw-border-slate-200 tw-bg-white"
                : "tw-border-dashed tw-border-slate-200 tw-bg-slate-50"
              } hover:tw-border-[#03234a]`}
          >
            <div className="tw-block tw-w-full">
              <SqlPreview
                value={hasRdvLineage ? editState.columnTransform : ""}
                placeholder={hasRdvLineage ? "Add transformation" : "No lineage"}
              />
            </div>
          </button>
        </td>
      </tr>
    );
  };

  const renderDangerZone = () => (
    <section className="tw-rounded-2xl tw-border tw-border-rose-200 tw-bg-rose-50 tw-p-3">
      <h3 className="tw-text-sm tw-font-semibold tw-text-rose-700">Danger zone</h3>
      <p className="tw-mt-1 tw-text-xs tw-leading-relaxed tw-text-rose-700">
        Removing this {isFactEntity ? "fact" : "dimension"} detaches relationships and
        updates connected lines.
      </p>
      {deleteEntityError ? (
        <p className="tw-mt-2 tw-text-xs tw-font-semibold tw-text-rose-700">
          {deleteEntityError}
        </p>
      ) : null}
      <button
        type="button"
        onClick={handleDeleteEntity}
        disabled={isDeletingEntity}
        className="tw-mt-3 tw-w-full tw-rounded-xl tw-border tw-border-rose-300 tw-bg-white tw-px-3 tw-py-2 tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide tw-text-rose-700 hover:tw-bg-rose-100 disabled:tw-cursor-not-allowed disabled:tw-opacity-60"
      >
        {isDeletingEntity
          ? "Deleting..."
          : isFactEntity
            ? "Delete Fact"
            : "Delete Dimension"}
      </button>
    </section>
  );

  const draftCount = draftRows.length;
  const editedCount = Object.keys(dirtyExistingColumns).length;
  const selectedRdvContextName = relatedRdvEntities.find(
    (option) => option.target_entity_rdv_id === entity.record.target_entity_rdv_id
  )?.target_entity_rdv_name;
  const shouldShowRdvSelector =
    !isInternalPromotedEntity || hasAssignedRdvLineage || showRdvLineageEditor;
  const mappingContextChips = selectedRdvContextName
    ? [selectedRdvContextName]
    : isInternalPromotedEntity && !hasAssignedRdvLineage
      ? ["Catalog asset"]
      : [];

  return (
    <div className="tw-relative tw-flex tw-h-full tw-w-full tw-flex-col tw-overflow-hidden tw-min-h-0">
      <div className="tw-sticky tw-top-0 tw-z-10 tw-flex tw-items-start tw-justify-between tw-border-b tw-border-slate-200 tw-bg-white/95 tw-p-5 tw-backdrop-blur-sm">
        <div className="tw-space-y-1">
          <span
            className={`tw-inline-flex tw-items-center tw-rounded-full tw-border tw-px-3 tw-py-1 tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide ${headerChipClasses}`}
          >
            {headerLabel}
          </span>
          <h2 className="tw-text-[1.7rem] tw-font-semibold tw-leading-tight tw-text-slate-900">
            {formatTitle(entity)}
          </h2>
        </div>
        <div className="tw-flex tw-flex-col tw-items-end tw-gap-2">
          <p className="tw-text-[0.65rem] tw-uppercase tw-tracking-[0.3em] tw-text-slate-500">
            {isMetadataDirty ? "Unsaved changes" : "Up to date"}
          </p>
          <div className="tw-flex tw-items-center tw-gap-2">
            <button
              type="button"
              className="tw-inline-flex tw-items-center tw-justify-center tw-rounded-full tw-border tw-border-slate-200 tw-bg-white tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-slate-700 hover:tw-bg-slate-100"
              onClick={handleClosePanel}
            >
              Close
            </button>
            <button
              type="button"
              onClick={() => void persistMetadata()}
              disabled={!isMetadataDirty || isSavingMetadata}
              className="tw-inline-flex tw-items-center tw-justify-center tw-rounded-full tw-border tw-border-[#03234a] tw-bg-[#03234a] tw-px-4 tw-py-2 tw-text-sm tw-font-semibold tw-text-white tw-transition hover:tw-bg-[#021b39] disabled:tw-cursor-not-allowed disabled:tw-opacity-60"
            >
              {isSavingMetadata ? "Saving..." : "Save"}
            </button>
          </div>
        </div>
      </div>
      <div className="tw-flex tw-flex-1 tw-w-full tw-flex-col tw-gap-5 tw-overflow-y-auto tw-p-5 tw-pr-3">
        {/* <nav className="tw-flex tw-flex-wrap tw-items-center tw-gap-3">
          {[
            { key: "details", label: "Entity Details" },
            { key: "mappings", label: "Columns & Relationships" }
          ].map((tab) => {
            const isActive = activeTab === tab.key;
            return (
              <button
                key={tab.key}
                type="button"
                onClick={() => setActiveTab(tab.key as typeof activeTab)}
                className={`tw-inline-flex tw-items-center tw-rounded-full tw-border tw-px-4 tw-py-2 tw-text-xs tw-font-semibold tw-uppercase tw-tracking-[0.2em] tw-transition ${
                  isActive
                    ? "tw-border-[#03234a] tw-bg-[#03234a] tw-text-white tw-shadow-lg"
                    : "tw-border-slate-200 tw-bg-white tw-text-slate-500 hover:tw-border-slate-300 hover:tw-text-slate-700"
                }`}
              >
                {tab.label}
              </button>
            );
          })}
        </nav> */}
        {activeTab === "details" && (
          <section className="tw-rounded-2xl tw-border tw-border-white tw-bg-white tw-p-4 tw-shadow-sm">
            {renderControlActions()}
            <p className="tw-mt-5 tw-text-[14px] tw-font-semibold tw-text-slate-1000">
              Metadata
            </p>
            <form className="tw-mt-3 tw-space-y-4" onSubmit={handleSaveMetadata}>
              <div className="tw-mt-1 tw-grid tw-gap-4">
                <MetadataInputField
                  label={
                    isFactEntity ? "Fact Name" : isDimensionEntity ? "Dimension Name" : "Target Entity Name"
                  }
                  value={metadataDraft.target_entity_dm_name}
                  onChange={(value) =>
                    setMetadataDraft((prev) => ({ ...prev, target_entity_dm_name: value }))
                  }
                  placeholder="Enter target entity name"
                />
                <MetadataInputField
                  label="Report View Name"
                  value={metadataDraft.target_entity_dm_report_view_name}
                  onChange={(value) =>
                    setMetadataDraft((prev) => ({
                      ...prev,
                      target_entity_dm_report_view_name: value
                    }))
                  }
                  placeholder="Report View entity name"
                />
                <MetadataSelectField
                  label="Data Mart"
                  value={metadataDraft.data_mart_id}
                  onChange={(value) => {
                    const selected = dataMartOptions.find(
                      (option) => String(option.data_mart_id) === value
                    );
                    setMetadataDraft((prev) => ({
                      ...prev,
                      data_mart_id: value,
                      datamart: selected?.data_mart ?? prev.datamart
                    }));
                  }}
                  options={dataMartSelectOptions}
                  placeholder={
                    isLoadingDataMarts
                      ? "Loading data marts..."
                      : dataMartSelectOptions.length > 0
                        ? "Select data mart"
                        : dataMartsError ?? "No data marts available"
                  }
                  disabled={isLoadingDataMarts || dataMartSelectOptions.length === 0}
                />
                <div className="tw--mt-1 tw-flex tw-justify-start">
                  {isAddingDataMart ? (
                    <div className="tw-mt-2 tw-w-full tw-flex tw-flex-col tw-gap-2 tw-rounded-2xl tw-border tw-border-slate-200 tw-bg-slate-50 tw-p-3">
                      <input
                        type="text"
                        value={newDataMartName}
                        onChange={(event) => {
                          setNewDataMartName(event.target.value);
                          if (dataMartCreateError) {
                            setDataMartCreateError(null);
                          }
                        }}
                        placeholder="Enter new data mart"
                        className={metadataInputClass}
                        disabled={isCreatingDataMart}
                      />
                      <div className="tw-flex tw-items-center tw-gap-2">
                        <button
                          type="button"
                          onClick={handleCreateDataMart}
                          className="tw-inline-flex tw-items-center tw-justify-center tw-rounded-full tw-bg-[#03234a] tw-px-3 tw-py-1.5 tw-text-xs tw-font-semibold tw-text-white hover:tw-bg-[#021b39] disabled:tw-cursor-not-allowed disabled:tw-bg-slate-300"
                          disabled={isCreatingDataMart}
                        >
                          {isCreatingDataMart ? "Saving..." : "Save"}
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setIsAddingDataMart(false);
                            setNewDataMartName("");
                            setDataMartCreateError(null);
                          }}
                          className="tw-inline-flex tw-items-center tw-justify-center tw-rounded-full tw-border tw-border-slate-300 tw-bg-white tw-px-3 tw-py-1.5 tw-text-xs tw-font-semibold tw-text-slate-600 hover:tw-border-slate-400 hover:tw-text-slate-800"
                          disabled={isCreatingDataMart}
                        >
                          Cancel
                        </button>
                      </div>
                      {dataMartCreateError ? (
                        <p className="tw-text-xs tw-text-rose-600">{dataMartCreateError}</p>
                      ) : null}
                    </div>
                  ) : (
                    <InlineCreateTextButton onClick={() => setIsAddingDataMart(true)}>
                      Add new data mart
                    </InlineCreateTextButton>
                  )}
                </div>
                {dataMartsError && (
                  <div className="tw-text-[11px] tw-font-semibold tw-text-rose-600">
                    <span>{dataMartsError}</span>
                    {onReloadDataMarts && (
                      <button
                        type="button"
                        onClick={onReloadDataMarts}
                        className="tw-ml-2 tw-text-[11px] tw-font-semibold tw-text-indigo-700"
                      >
                        Retry
                      </button>
                    )}
                  </div>
                )}
                {isDimensionEntity ? (
                  <MetadataSelectField
                    label="Table Type"
                    value={metadataDraft.table_construct}
                    onChange={(value) =>
                      setMetadataDraft((prev) => applyDimensionTableConstructChoice(prev, value))
                    }
                    options={DIMENSION_TABLE_CONSTRUCT_OPTIONS}
                  />
                ) : (
                  <MetadataInputField
                    label="Table Type"
                    value={
                      isFactEntity && !metadataDraft.table_construct
                        ? FACT_TABLE_CONSTRUCT_DEFAULT
                        : metadataDraft.table_construct
                    }
                    onChange={(value) =>
                      setMetadataDraft((prev) => ({ ...prev, table_construct: value }))
                    }
                    placeholder={FACT_TABLE_CONSTRUCT_DEFAULT}
                    readOnly={isFactEntity}
                  />
                )}
                {isDimensionEntity && metadataDraft.table_construct === "SCD2_V2_as_table" ? (
                  <div className="tw-group tw-flex tw-flex-col tw-gap-1.5 tw-border-b tw-border-slate-100 tw-pb-4">
                    <div className="tw-flex tw-w-fit tw-items-center tw-gap-2">
                      <ToggleControl
                        label="Include start data"
                        checked={metadataDraft.include_start_record}
                        onChange={(checked) =>
                          setMetadataDraft((prev) => ({ ...prev, include_start_record: checked }))
                        }
                        compact={false}
                      />
                      <span
                        tabIndex={0}
                        aria-label="Includes a start record dated 1900-01-01 in the SCD2 dimension."
                        className="tw-group/info tw-relative tw-inline-flex tw-text-slate-400 hover:tw-text-slate-600 focus:tw-outline-none"
                      >
                        <FiInfo className="tw-h-4 tw-w-4" aria-hidden="true" />
                        <span
                          role="tooltip"
                          className="tw-pointer-events-none tw-absolute tw-left-full tw-top-1/2 tw-z-20 tw-ml-2 tw-whitespace-nowrap tw--translate-y-1/2 tw-rounded-lg tw-bg-slate-900 tw-px-3 tw-py-2 tw-text-xs tw-font-medium tw-normal-case tw-tracking-normal tw-text-white tw-opacity-0 tw-shadow-lg tw-transition-opacity tw-duration-100 group-hover/info:tw-opacity-100 group-focus/info:tw-opacity-100"
                        >
                          Includes a start record dated 1900-01-01
                          <br />
                          in the SCD2 dimension.
                        </span>
                      </span>
                    </div>
                  </div>
                ) : null}
                {showDistributionControls ? (
                  <>
                    <MetadataSelectField
                      label="Distribution type (Synapse SQL pool only)"
                      value={normalizedDistributionType}
                      onChange={(value) =>
                        setMetadataDraft((prev) => ({
                          ...prev,
                          distribution_type: value
                        }))
                      }
                      options={DISTRIBUTION_TYPE_OPTIONS}
                      placeholder="Select distribution type"
                    />
                    {isDistributionKeyEditable ? (
                      <MetadataInputField
                        label="Distribution key *"
                        value={metadataDraft.distribution_key}
                        onChange={(value) =>
                          setMetadataDraft((prev) => ({ ...prev, distribution_key: value }))
                        }
                        placeholder="Enter distribution key column"
                      />
                    ) : null}
                  </>
                ) : null}
                {shouldShowRdvSelector ? (
                  <>
                    <MetadataSelectField
                      label={`Select RDV Entity${isDimensionEntity ? " " : ""}`}
                      value={metadataDraft.target_entity_rdv_id}
                      onChange={(value) =>
                        setMetadataDraft((prev) => ({ ...prev, target_entity_rdv_id: value }))
                      }
                      options={rdvSelectOptions}
                      placeholder={
                        isLoadingRdvEntities
                          ? "Loading RDV entities..."
                          : rdvSelectOptions.length > 0
                            ? "Select RDV entity"
                            : rdvEntitiesError ?? "No RDV entities available"
                      }
                      disabled={isLoadingRdvEntities || rdvSelectOptions.length === 0}
                    />
                    <SqlEditorField
                      label="Data Vault Source Filter"
                      value={metadataDraft.target_entity_rdv_source_filter}
                      onChange={(value) =>
                        setMetadataDraft((prev) => ({
                          ...prev,
                          target_entity_rdv_source_filter: value
                        }))
                      }
                      placeholder="Enter RDV source filter"
                      autocomplete={sqlAutocomplete}
                    />
                  </>
                ) : (
                  <div className="tw-rounded-2xl tw-border tw-border-slate-200 tw-bg-slate-50 tw-p-4">
                    <p className="tw-text-[0.6rem] tw-font-semibold tw-uppercase tw-tracking-[0.3em] tw-text-slate-500">
                      RDV Source
                    </p>
                    <p className="tw-mt-2 tw-text-sm tw-font-medium tw-text-slate-700">
                      Optional. Add RDV source later if lineage exists.
                    </p>
                    <button
                      type="button"
                      onClick={() => setShowRdvLineageEditor(true)}
                      className="tw-mt-3 tw-inline-flex tw-items-center tw-gap-1 tw-appearance-none tw-border-0 tw-bg-transparent tw-p-0 tw-text-left tw-text-[13px] tw-font-semibold tw-text-[#03234a] tw-underline tw-decoration-[#03234a]/35 tw-underline-offset-2 hover:tw-text-[#021b39] hover:tw-decoration-[#021b39] focus:tw-outline-none"
                    >
                      <span aria-hidden="true" className="tw-text-[15px] tw-leading-none">+</span>
                      Add RDV source
                    </button>
                  </div>
                )}
                {rdvEntitiesError && (
                  <div className="tw-text-[11px] tw-font-semibold tw-text-rose-600">
                    <span>{rdvEntitiesError}</span>
                    {onReloadRdvEntities && (
                      <button
                        type="button"
                        onClick={onReloadRdvEntities}
                        className="tw-ml-2 tw-text-[11px] tw-font-semibold tw-text-indigo-700"
                      >
                        Retry
                      </button>
                    )}
                  </div>
                )}
                <MetadataInputField
                  label="Target Schema"
                  value={metadataDraft.target_schema}
                  onChange={(value) =>
                    setMetadataDraft((prev) => ({ ...prev, target_schema: value }))
                  }
                  placeholder="analytics"
                />
                <MetadataInputField
                  label="Description"
                  value={metadataDraft.description}
                  onChange={(value) =>
                    setMetadataDraft((prev) => ({ ...prev, description: value }))
                  }
                  placeholder="Optional description"
                  multiline
                />
                {/* "Report View Source Filter" is hidden until the report-view feature pack: the
                    report view is a layer after the data mart, and generation no longer applies
                    this filter to the dimension itself. The dimension's filter is "Data Vault
                    Source Filter" above. See wade-orchestration docs/report_view_layer.md. */}
                <SqlEditorField
                  label="DQ Source Filter"
                  value={metadataDraft.dq_source_filter}
                  onChange={(value) =>
                    setMetadataDraft((prev) => ({ ...prev, dq_source_filter: value }))
                  }
                  placeholder="Enter DQ source filter"
                  autocomplete={sqlAutocomplete}
                />
              </div>
              <MetadataActionFooter
                onSave={() => void persistMetadata()}
                canSave={isMetadataDirty}
                isSaving={isSavingMetadata}
                isDirty={isMetadataDirty}
                errorMessage={metadataError}
                successMessage={null}
                showSaveButton={false}
              />
            </form>
            <div className="tw-mt-4">{renderDangerZone()}</div>
          </section>
        )}

        {activeTab === "mappings" && (
          <>
            <section className="tw-rounded-2xl tw-border tw-border-white tw-bg-white tw-p-4 tw-shadow-sm">

              {columnLoadError ? (
                <p className="tw-mt-2 tw-text-xs tw-font-semibold tw-text-rose-600">
                  {columnLoadError}
                </p>
              ) : null}
              <div className="tw-mt-4 tw-space-y-2">
                {filteredColumns.slice(0, 5).map((column) => (
                  <div
                    key={column.record.target_entity_dm_column_id ?? column.record.target_entity_dm_column_name}
                    className="tw-rounded-xl tw-border tw-border-slate-100 tw-bg-slate-50 tw-p-3"
                  >
                    <div className="tw-flex tw-items-center tw-justify-between">
                      <p className="tw-text-sm tw-font-semibold tw-text-slate-800">
                        {column.record.target_entity_dm_column_name}
                      </p>
                      <span
                        className={`tw-rounded-full tw-px-2 tw-py-0.5 tw-text-[10px] tw-font-semibold ${roleBadgeColor[column.role]}`}
                      >
                        {roleLabel[column.role]}
                      </span>
                    </div>
                    {column.record.description && (
                      <p className="tw-mt-1 tw-text-xs tw-text-slate-500">{column.record.description}</p>
                    )}
                  </div>
                ))}
                {filteredColumns.length > 5 && (
                  <p className="tw-text-xs tw-font-semibold tw-text-slate-500">
                    +{filteredColumns.length - 5} more columns in this entity.
                  </p>
                )}
                {/* {filteredColumns.length === 0 && (
                  <p className="tw-text-sm tw-text-slate-500">
                    No columns match your current filters. Open the editor to add new metadata.
                  </p>
                )} */}
              </div>
            </section>

            <section className="tw-rounded-2xl tw-border tw-border-white tw-bg-white tw-p-4 tw-shadow-sm">
              <p className="tw-text-[11px] tw-font-semibold tw-uppercase tw-text-slate-500">
                Relationships
              </p>
              <div className="tw-mt-3">{renderRelationships()}</div>
            </section>
          </>
        )}
      </div>
      {isColumnModalOpen && typeof document !== "undefined"
        ? createPortal(
          <ColumnMappingModalShell
            title={`Column Mappings - ${formatTitle(entity)}`}
            contextLabel="Source entity"
            contextChips={mappingContextChips}
            toolbar={
              <>
                {isFactEntity ? (
                  <button
                    type="button"
                    onClick={handleAddManualFactDraftRow}
                    className="tw-inline-flex tw-items-center tw-gap-1 tw-rounded-full tw-border tw-border-slate-200 tw-bg-white tw-px-3 tw-py-2 tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide tw-text-slate-700 focus:tw-outline-none focus:tw:ring-1 focus:tw:ring-slate-300"
                  >
                    <span aria-hidden="true" className="tw-text-[15px] tw-leading-none">+</span>
                    New fact column
                  </button>
                ) : null}
                <select
                  value={modalColumnViewFilter}
                  onChange={(event) =>
                    setModalColumnViewFilter(event.target.value as ModalColumnViewFilter)
                  }
                  className="tw-rounded-full tw-border tw-border-slate-200 tw-bg-white tw-px-3 tw-py-2 tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide tw-text-slate-700 focus:tw-outline-none focus:tw:ring-1 focus:tw:ring-slate-300"
                >
                  <option value="all">All rows</option>
                  <option value="mapped">Mapped</option>
                  <option value="available">Available</option>
                  <option value="inherited">Inherited</option>
                  <option value="dim-key">Dim keys</option>
                </select>
                <input
                  type="text"
                  value={modalColumnSearchQuery}
                  onChange={(event) => setModalColumnSearchQuery(event.target.value)}
                  placeholder="Search columns..."
                  className="tw-w-56 tw-rounded-full tw-border tw-border-slate-200 tw-bg-white tw-px-3 tw-py-2 tw-text-sm tw-text-slate-700 focus:tw-outline-none focus:tw:ring-1 focus:tw:ring-slate-300"
                />
                <div className="tw-rounded-full tw-bg-slate-100 tw-px-3 tw-py-2 tw-text-[11px] tw-font-semibold tw-uppercase tw-tracking-wide tw-text-slate-600">
                  {draftCount} {draftCount === 1 ? "available row" : "available rows"}
                </div>
                {pendingColumnCount > 0 ? (
                  <div
                    className="tw-rounded-full tw-bg-amber-100 tw-px-3 tw-py-2 tw-text-[11px] tw-font-semibold tw-uppercase tw-tracking-wide tw-text-amber-800"
                    title="Nothing is written until you press Save columns."
                  >
                    {describePending(pendingColumnCount)}
                  </div>
                ) : null}
                <div className="tw-rounded-full tw-bg-slate-100 tw-px-3 tw-py-2 tw-text-[11px] tw-font-semibold tw-uppercase tw-tracking-wide tw-text-slate-600">
                  {editedCount} {editedCount === 1 ? "edited row" : "edited rows"}
                </div>
              </>
            }
            actionCard={
              <>
                <ColumnMappingActionCard
                  saveBehaviorText="Save all edited rows."
                  onClose={() => {
                    // Closing discards pending work, so leaving has to say so — same rule
                    // as the RDV modal.
                    if (
                      pendingColumnCount > 0 &&
                      !window.confirm(
                        `${describePending(pendingColumnCount)} on this entity. Close without saving?`
                      )
                    ) {
                      return;
                    }
                    closeColumnModal();
                  }}
                  onSave={handlePersistColumns}
                  saveLabel={
                    pendingColumnCount > 0
                      ? `Save columns (${pendingColumnCount})`
                      : "Save columns"
                  }
                  isSaving={isPersistingColumns}
                  disabled={
                    isPersistingColumns ||
                    (!draftRows.length && !hasDirtyExistingColumns) ||
                    !effectiveTargetEntityDmId
                  }
                  errorMessage={columnPersistError}
                />
                {!effectiveTargetEntityDmId ? (
                  <p className="tw-max-w-[20rem] tw-text-right tw-text-[11px] tw-font-semibold tw-text-amber-600">
                    Save the entity metadata before persisting column mappings.
                  </p>
                ) : null}
              </>
            }
          >
            <div className="tw-max-h-[64vh] tw-overflow-auto tw-rounded-2xl tw-border tw-border-slate-200 tw-shadow-sm">
              <table className="tw-min-w-full tw-table-fixed tw-text-xs tw-text-slate-700 tw-text-center">
                <thead className="tw-bg-slate-50 tw-text-center">
                  <tr>
                    <th className="tw-whitespace-nowrap tw-px-3 tw-py-2 tw-font-semibold">
                      Source Column
                    </th>
                    <th className="tw-w-28 tw-whitespace-nowrap tw-px-3 tw-py-2 tw-font-semibold">
                      Origin
                    </th>
                    <th className="tw-whitespace-nowrap tw-px-3 tw-py-2 tw-font-semibold">
                      Target Column
                    </th>
                    <th className="tw-w-24 tw-whitespace-nowrap tw-px-3 tw-py-2 tw-text-center tw-font-semibold">
                      Dim Key
                    </th>
                    <th className="tw-w-28 tw-whitespace-nowrap tw-px-3 tw-py-2 tw-text-center tw-font-semibold">
                      Dim Attribute
                    </th>
                    <th className="tw-w-20 tw-whitespace-nowrap tw-px-3 tw-py-2 tw-text-center tw-font-semibold">
                      PII
                    </th>
                    <th className="tw-whitespace-nowrap tw-px-3 tw-py-2 tw-font-semibold">
                      Description
                    </th>
                    <th className="tw-whitespace-nowrap tw-px-3 tw-py-2 tw-font-semibold">
                      Derivation Transform
                    </th>
                  </tr>
                </thead>
                <tbody className="tw-divide-y tw-divide-slate-100 tw-bg-white">
                  {prioritizedExistingModalColumns.map((column) => renderExistingRow(column))}
                  {prioritizedInheritedBkDraftRows.map((row) => renderDraftRow(row))}
                  {remainingExistingModalColumns.map((column) => renderExistingRow(column))}
                  {remainingDraftModalRows.map((row) => renderDraftRow(row))}
                  {filteredDraftModalRows.length === 0 && filteredExistingModalColumns.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="tw-px-3 tw-py-6 tw-text-center tw-text-sm tw-text-slate-500">
                        No columns match the current filters.
                      </td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </div>
            <div className="tw-mt-3 tw-flex tw-items-center tw-justify-between tw-gap-3">
              <p className="tw-text-xs tw-text-slate-500">
                Showing {filteredExistingModalColumns.length + filteredDraftModalRows.length} of{" "}
                {filteredColumns.length + draftRows.length} rows
              </p>
            </div>
          </ColumnMappingModalShell>,
          document.body
        )
        : null}
      {controlDrawerOpen && (
        <>
          <div
            className="tw-absolute tw-inset-0 tw-z-40 tw-bg-slate-900/20"
            onClick={() => setControlDrawerOpen(false)}
          />
          <div className="tw-absolute tw-right-0 tw-top-0 tw-z-50 tw-h-full tw-w-72 tw-bg-white tw-shadow-2xl">
            <div className="tw-flex tw-items-center tw-justify-between tw-border-b tw-border-slate-200 tw-px-4 tw-py-3">
              <div>
                <p className="tw-text-[0.75rem] tw-font-semibold tw-uppercase tw-tracking-[0.3em] tw-text-slate-400">
                  Entity Controls
                </p>
                <p className="tw-text-sm tw-font-semibold tw-text-slate-900">{headerLabel}</p>
              </div>
              <button
                type="button"
                onClick={() => setControlDrawerOpen(false)}
                className="tw-inline-flex tw-h-8 tw-w-8 tw-items-center tw-justify-center tw-rounded-full tw-border tw-border-slate-200 tw-text-slate-600 hover:tw-bg-slate-100"
              >
                ✕
              </button>
            </div>
            <div className="tw-p-4 tw-space-y-4">
              <ToggleControl
                label="Include DQ Test"
                checked={includeDqEnabled}
                onChange={handleIncludeDqToggle}
              />
            </div>
          </div>
        </>
      )}
      {transformEditor
        ? createPortal(
          <div className="tw-fixed tw-inset-0 tw-z-[3200] tw-flex tw-items-center tw-justify-center tw-bg-slate-900/60 tw-px-6 tw-py-12 tw-backdrop-blur-sm">
            <div className="tw-w-full tw-max-w-3xl tw-rounded-2xl tw-border tw-border-slate-200 tw-bg-white tw-p-6 tw-shadow-[0_60px_120px_-40px_rgba(15,23,42,0.65)]">
              <h3 className="tw-text-base tw-font-semibold tw-text-slate-900">
                {transformEditor.title}
              </h3>
              <p className="tw-mt-1 tw-text-xs tw-text-slate-500">
                {transformEditor.readOnly
                  ? "This transform is currently read-only."
                  : "Paste or edit the RDV to Data Mart derivation SQL for this column."}
              </p>
              <div className="tw-mt-4">
                <SqlEditorField
                  label="Derivation SQL"
                  value={transformEditor.value}
                  onChange={handleTransformEditorChange}
                  placeholder="SELECT …"
                  readOnly={Boolean(transformEditor.readOnly)}
                  height="256px"
                />
              </div>
              <div className="tw-mt-4 tw-flex tw-justify-end tw-gap-3">
                <button
                  type="button"
                  onClick={handleTransformEditorCancel}
                  className="tw-rounded-full tw-border tw-border-slate-200 tw-bg-white tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-slate-600 hover:tw-bg-slate-100"
                >
                  Close
                </button>
                {!transformEditor.readOnly && (
                  <button
                    type="button"
                    onClick={handleTransformEditorSave}
                    className="tw-rounded-full tw-bg-[#03234a] tw-px-4 tw-py-2 tw-text-sm tw-font-semibold tw-text-white hover:tw-bg-[#021b39]"
                  >
                    Save
                  </button>
                )}
              </div>
            </div>
          </div>,
          document.body
        )
        : null}
      {descriptionEditor
        ? createPortal(
          <div className="tw-fixed tw-inset-0 tw-z-[3100] tw-flex tw-items-center tw-justify-center tw-bg-slate-900/60 tw-px-6 tw-py-12 tw-backdrop-blur-sm">
            <div className="tw-w-full tw-max-w-3xl tw-rounded-2xl tw-border tw-border-slate-200 tw-bg-white tw-p-6 tw-shadow-[0_60px_120px_-40px_rgba(15,23,42,0.65)]">
              <h3 className="tw-text-base tw-font-semibold tw-text-slate-900">
                {descriptionEditor.title}
              </h3>
              <textarea
                className={`tw-mt-4 tw-min-h-[10rem] tw-w-full tw-rounded-2xl tw-border tw-border-slate-200 tw-bg-slate-50 tw-p-4 tw-text-sm tw-text-slate-800 focus:tw-border-[#03234a] focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-[#03234a]/20 ${descriptionEditor.readOnly ? "tw-cursor-not-allowed tw-bg-slate-100" : ""
                  }`}
                value={descriptionEditor.value}
                onChange={(event) => handleDescriptionEditorChange(event.target.value)}
                placeholder="Add column context"
                readOnly={Boolean(descriptionEditor.readOnly)}
              />
              <div className="tw-mt-4 tw-flex tw-justify-end tw-gap-3">
                <button
                  type="button"
                  onClick={() => setDescriptionEditor(null)}
                  className="tw-rounded-full tw-border tw-border-slate-200 tw-bg-white tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-slate-600 hover:tw-bg-slate-100"
                >
                  Close
                </button>
                {!descriptionEditor.readOnly ? (
                  <button
                    type="button"
                    onClick={handleDescriptionEditorSave}
                    className="tw-rounded-full tw-bg-[#03234a] tw-px-4 tw-py-2 tw-text-sm tw-font-semibold tw-text-white hover:tw-bg-[#021b39]"
                  >
                    Save
                  </button>
                ) : null}
              </div>
            </div>
          </div>,
          document.body
        )
        : null}
      {validationPopupMessage && typeof document !== "undefined"
        ? createPortal(
          <div className="tw-fixed tw-inset-0 tw-z-[3300] tw-flex tw-items-center tw-justify-center tw-bg-slate-900/45 tw-px-6 tw-py-12 tw-backdrop-blur-sm">
            <div className="tw-w-full tw-max-w-md tw-rounded-2xl tw-border tw-border-rose-100 tw-bg-white tw-p-6 tw-shadow-[0_60px_120px_-40px_rgba(15,23,42,0.65)]">
              <p className="tw-text-[0.68rem] tw-font-semibold tw-uppercase tw-tracking-[0.28em] tw-text-rose-600">
                Validation failed
              </p>
              <h3 className="tw-mt-2 tw-text-lg tw-font-semibold tw-text-slate-900">
                Dimension details incomplete
              </h3>
              <p className="tw-mt-3 tw-text-sm tw-leading-6 tw-text-slate-600">
                {validationPopupMessage}
              </p>
              <div className="tw-mt-5 tw-flex tw-justify-end">
                <button
                  type="button"
                  onClick={() => setValidationPopupMessage(null)}
                  className="tw-rounded-full tw-bg-[#03234a] tw-px-4 tw-py-2 tw-text-sm tw-font-semibold tw-text-white hover:tw-bg-[#021b39]"
                >
                  OK
                </button>
              </div>
            </div>
          </div>,
          document.body
        )
        : null}
    </div>
  );
}
